// switchAxesUtils.ts
//The code is called in ChartType.tsx handles all the changes of Charts

import { useSelector } from 'react-redux';
import ChartsInfo from '../../ChartAxes/ChartsInfo2';
import { fieldName, findNewDisplayName } from '../../CommonFunctions/CommonFunctions';
import { getFieldsToChartAllowedNumbers } from './chartUtils';
import { RootState } from '../../../redux';
import { AxisType } from '../../CommonFunctions/aliases';
import { setDisplayName } from '../../ChartAxes/setDisplayName';
import { setPrefix } from '../../ChartAxes/SetPrefix';
import { toast } from 'react-toastify';
import { updateFormatOption } from '../../../redux/ChartPoperties/ChartControlsActions';
import { useDispatch } from 'react-redux';

/**
 * Custom hook that manages the switching of chart axes based on the selected chart type.
 * @returns A function that handles the logic for switching chart axes based on the old and new chart types.
 *
 * Handles special date field transformations: When switching from table/crosstab charts, automatically converts min/max date aggregations to count aggregations for measure fields. For scatter plot chart (which have no measure field), the date transformation logic is applied specifically to X and Y axis fields instead of measure fields.
 */
export const useSwitchAxesForCharts = (
  propKey: string,
  keepOldData: (propKey: string, keepData: boolean) => void,
  updateChartData: (propKey: string, data: any) => void,
  hasChartData: (propKey: string) => boolean,
) => {
  const dispatch = useDispatch();
  const chartControls = useSelector((state: RootState) => state.chartControls);
  const chartProp = useSelector((state: RootState) => state.chartProperties);

  const switchAxesForCharts = (oldChart: string, newChart: string) => {
    let measureModified = false;
    const targetAxis = [AxisType.Measure, AxisType.X, AxisType.Y];
    let axes = JSON.parse(JSON.stringify(chartProp.properties[propKey].chartAxes || []));
    const chartType = chartProp.properties[propKey].chartType;
    const chartAxes = chartProp.properties[propKey].chartAxes;
    let newNotAllowednames: string[] = [];
    axes.forEach((axis: any) => {
      if (!targetAxis.includes(axis.name)) return;

      if (Array.isArray(axis.fields)) {
        axis.fields = axis.fields.map((field: any) => {
          if (field?.windowfn) {
            measureModified = true;
            if (field.isTextRenamed) return { ...field, windowfn: null };
            const newFieldData = setDisplayName(
              setPrefix({ ...field, windowfn: null }, axis.name, chartType),
              axis.name,
              chartType,
            );
            newFieldData.displayname = findNewDisplayName(
              chartProp.properties[propKey].chartAxes,
              newFieldData,
              chartControls.properties?.[propKey]?.formatOptions?.tooltipFormats,
              newNotAllowednames,
            );
            newNotAllowednames.push(newFieldData.displayname);
            return {
              ...newFieldData,
              windowfn: null,
            };
          }
          return field;
        });
      }
    });
    let oldChartAxes = axes;
    // let oldChartAxes = JSON.parse(JSON.stringify(chartProp.properties[propKey].chartAxes || []));
    let newChartAxes: any = [];
    for (let i = 0; i < ChartsInfo[newChart].dropZones.length; i++) {
      newChartAxes.push({
        name: ChartsInfo[newChart].dropZones[i].name,
        fields: [],
      });
    }
    const IsOldChartFieldsCountIsGreater = (newChartName: string, chartAxesIndex: number) => {
      let oldChartAxesFieldsCount =
        chartProp.properties[propKey].chartAxes[chartAxesIndex]?.fields?.length;
      let newAllowedNumbers = ChartsInfo[newChartName].dropZones[chartAxesIndex].allowedNumbers;

      return Number(oldChartAxesFieldsCount) > Number(newAllowedNumbers);
    };

    const UpdateChartDataWithNewChartAxes = (oldAxes: any, newAxes: any, index: number) => {
      let chartData: any = [],
        keys: any = [],
        keysToRemove: any = [];

      if (
        chartControls.properties[propKey].chartData &&
        chartControls.properties[propKey].chartData.length > 0
      ) {
        chartData = JSON.parse(JSON.stringify(chartControls.properties[propKey].chartData));
      }

      newAxes[index].fields.forEach((newField: any) => {
        keys.push(newField.fieldname);
      });

      oldAxes[index].fields.forEach((field: any) => {
        if (!keys.includes(field.fieldname)) {
          keysToRemove.push(fieldName(field));
        }
      });

      chartData.forEach((data: any) => {
        keysToRemove.forEach((key: string) => {
          delete data[key];
        });
      });

      updateChartData(propKey, chartData);
    };

    //returns allowed measure field from chartInfo2 file
    function getAllowedMeasureFields(chartName: string) {
      let chartInfo = ChartsInfo[chartName]?.dropZones.find((axis) => axis.name === 'Measure');
      const allowedFields = chartInfo ? chartInfo.allowedNumbers : 1;
      return allowedFields;
    }

    // allows to change agg and displayname for min, max date
    function convertDateMinMaxToCount(
      fields: any[],
      allowedFields: number,
      existingNames: string[] = [],
    ) {
      const newFields = [...fields];
      let callQueryAPI = false;
      const measureFormatUpdates: Record<string, { roundingDigits: number }> = {};
      let notAllowedNames: string[] = [...existingNames];
      for (let i = 0; i < newFields.length; i++) {
        if (
          newFields[i].timeGrain === 'date' &&
          (newFields[i].agg === 'max' || newFields[i].agg === 'min')
        ) {
          callQueryAPI = true;
          newFields[i].agg = 'count';
          const updatedField = setDisplayName(newFields[i], 'Measure', chartType);
          const uniqueDisplayName = findNewDisplayName(
            chartAxes,
            updatedField,
            undefined,
            notAllowedNames,
          );
          notAllowedNames.push(uniqueDisplayName);
          newFields[i].displayname = uniqueDisplayName;
          // Collect the update for this measure
          measureFormatUpdates[newFields[i].uId] = {
            roundingDigits: 0,
          };
        }
        if (i + 1 === allowedFields) {
          newFields.splice(allowedFields);
          break;
        }
      }
      // Dispatch all updates at once if there are any
      if (Object.keys(measureFormatUpdates).length > 0) {
        ['labelFormats', 'tooltipFormats'].forEach((formatName) => {
          const formatObject = chartControls.properties[propKey].formatOptions[formatName];
          const updatedMeasureFormats = { ...formatObject.measureFormats };
          for (const uId of Object.keys(measureFormatUpdates)) {
            updatedMeasureFormats[uId] = {
              ...updatedMeasureFormats[uId],
              ...measureFormatUpdates[uId],
            };
          }
          dispatch(
            updateFormatOption(propKey, formatName, 'measureFormats', updatedMeasureFormats),
          );
        });
      }

      return { newFields, callQueryAPI, notAllowedNames };
    }

    function assignWithLimitCheck(newChart: string, axisIndex: number, arr1: any[], arr2?: any[]) {
      const allowed = getFieldsToChartAllowedNumbers(newChart, axisIndex, arr1, arr2);

      const totalIncoming = [...(arr1 || []), ...(arr2 || [])];

      if (allowed.length < totalIncoming.length) {
        toast.warning('Extra fields are removed', {
          position: 'top-center',
          autoClose: 10000,
          toastId: 'chart-data-limit-warning',
          style: { top: '10px' },
        });
      }

      return allowed;
    }

    switch (oldChart) {
      case 'line':
      case 'area':
      case 'stackedArea':
        if (['line', 'area', 'stackedArea'].includes(newChart)) {
          keepOldData(propKey, false);
          return { newChartAxes: oldChartAxes, callAPI: measureModified };
        }

        if (['multibar', 'stackedBar', 'horizontalBar', 'horizontalStacked'].includes(newChart)) {
          keepOldData(propKey, false);

          const mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];

          if (oldChartAxes[0].fields.length > 0) {
            newChartAxes[0].fields = oldChartAxes[0].fields;
          }

          if (mergedFields.length === 1) {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(newChart, 1, [mergedFields[0]]);
          }

          if (mergedFields.length > 1) {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(newChart, 1, [mergedFields[0]]);

            newChartAxes[2].fields = getFieldsToChartAllowedNumbers(newChart, 2, [mergedFields[1]]);
          }

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[3].fields = assignWithLimitCheck(newChart, 3, oldChartAxes[3].fields);
          }

          const finalPlacedFields = [
            ...(newChartAxes[1]?.fields || []),
            ...(newChartAxes[2]?.fields || []),
          ];

          const removedFields = mergedFields.filter((field) => !finalPlacedFields.includes(field));

          if (removedFields.length > 0) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }

          let callAPI = measureModified;
          if (
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length !== oldChartAxes[2].fields.length ||
            newChartAxes[3].fields.length !== oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (['waterfall', 'treeMap', 'sankey', 'filledMap'].includes(newChart)) {
          keepOldData(propKey, false);

            let mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];

          if (oldChartAxes[0].fields.length > 0) {
            newChartAxes[0].fields = oldChartAxes[0].fields;
          }

          if (mergedFields.length > 0) {
            if (newChart === 'filledMap') {
              const textFields = mergedFields.filter((f: any) => f.dataType === 'text');

              if (textFields.length === 0 && mergedFields.length > 0) {
                toast.warning('Non-compatible fields are removed', {
                  position: 'top-center',
                  autoClose: 10000,
                  toastId: 'chart-data-limit-warning',
                  style: { top: '10px' },
                });
              }else if (textFields.length > 0 && textFields.length !== mergedFields.length) {
      toast.warning('Extra fields are removed', {
        position: 'top-center',
        autoClose: 10000,
        toastId: 'chart-data-limit-warning',
        style: { top: '10px' },
      });
    }

              newChartAxes[1].fields = assignWithLimitCheck(newChart, 1, textFields);
            } else {
              newChartAxes[1].fields = assignWithLimitCheck(
                newChart,
                1,
                oldChartAxes[1].fields,
                oldChartAxes[2].fields,
              );
            }
          }

          if (oldChartAxes[1].fields.length === 0 && oldChartAxes[2].fields.length > 0) {
            newChartAxes[1].fields = assignWithLimitCheck(newChart, 1, oldChartAxes[2].fields);
          }

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[2].fields = assignWithLimitCheck(newChart, 2, oldChartAxes[3].fields);
          }
          let callAPI = measureModified;
          if (
            oldChartAxes[2].fields.length > 0 ||
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length !== oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'scatterPlot') {
          keepOldData(propKey, false);
          const mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];

          if (oldChartAxes[0].fields.length > 0) {
            newChartAxes[0].fields = oldChartAxes[0].fields;
          }

          if (mergedFields.length >= 1) {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(newChart, 1, mergedFields);
          }

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[2].fields = getFieldsToChartAllowedNumbers(
              newChart,
              2,
              oldChartAxes[3].fields,
            );

            if (oldChartAxes[3].fields.length > 1)
              newChartAxes[3].fields = [oldChartAxes[3].fields[1]];
          }

          const finalPlacedFields = [
            ...(newChartAxes[1]?.fields || []),
            ...(newChartAxes[2]?.fields || []),
          ];

          const removedFields = mergedFields.filter((field) => !finalPlacedFields.includes(field));

          if (removedFields.length > 0 || oldChartAxes[3].fields.length > 2) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }
          let callAPI = measureModified;
          if (
            oldChartAxes[2].fields.length > 0 ||
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length + newChartAxes[3].fields.length !==
              oldChartAxes[3].fields.length ||
            oldChartAxes[3].fields.length !== 2
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (
          newChart === 'pie' ||
          newChart === 'donut' ||
          newChart === 'rose' ||
          newChart === 'wordCloud'
        ) {
          keepOldData(propKey, false);

          if (oldChartAxes[0].fields.length > 0) {
            newChartAxes[0].fields = oldChartAxes[0].fields;
          }

          newChartAxes[1].fields = assignWithLimitCheck(
            newChart,
            1,
            oldChartAxes[1].fields,
            oldChartAxes[2].fields,
          );

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[2].fields = assignWithLimitCheck(newChart, 2, oldChartAxes[3].fields);
          }

          let callAPI = measureModified;
          if (
            oldChartAxes[2].fields.length > 0 ||
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length !== oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'table') {
          keepOldData(propKey, false);

          const mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];

          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          if (oldChartAxes[1].fields.length > 0) {
            newChartAxes[1].fields = oldChartAxes[1].fields;
          }

          if (mergedFields) {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(newChart, 1, mergedFields);
          }

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[2].fields = getFieldsToChartAllowedNumbers(
              newChart,
              2,
              oldChartAxes[3].fields,
            );
          }

          let callAPI = measureModified;
          if (
            oldChartAxes[2].fields.length > 0 ||
            newChartAxes[2].fields.length !== oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'heatmap' || newChart === 'boxPlot') {
          keepOldData(propKey, false);

          const mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          if (mergedFields.length === 1) {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(newChart, 1, [mergedFields[0]]);
          }

          if (mergedFields.length > 1) {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(newChart, 1, [mergedFields[0]]);

            newChartAxes[2].fields = getFieldsToChartAllowedNumbers(newChart, 2, [mergedFields[1]]);
          }

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[3].fields = assignWithLimitCheck(newChart, 3, oldChartAxes[3].fields);
          }

          let callAPI = measureModified;
          if (
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length !== oldChartAxes[2].fields.length ||
            newChartAxes[3].fields.length !== oldChartAxes[3].fields.length ||
            newChartAxes[2].fields.length === 0
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'crossTab') {
          keepOldData(propKey, false);

          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          const mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];

          if (mergedFields.length >= 1) {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(newChart, 1, mergedFields);
          }

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[3].fields = assignWithLimitCheck(newChart, 3, oldChartAxes[3].fields);
          }

          let callAPI = measureModified;
          if (
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length !== oldChartAxes[2].fields.length ||
            newChartAxes[3].fields.length !== oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (['gauge', 'funnel', 'simplecard', 'richText'].includes(newChart)) {
          keepOldData(propKey, false);

          const mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];

          if (mergedFields.length > 0) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }

          if (oldChartAxes[0].fields.length > 0) {
            newChartAxes[0].fields = oldChartAxes[0].fields;
          }

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[1].fields = assignWithLimitCheck(newChart, 1, oldChartAxes[3].fields);
          }

          let callAPI = measureModified;
          if (
            oldChartAxes[1].fields.length > 0 ||
            oldChartAxes[2].fields.length > 0 ||
            newChartAxes[1].fields.length !== oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'calendar') {
          keepOldData(propKey, false);

          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          let callAPI = measureModified;

          const mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];

          if (mergedFields.length > 0) {
            const dateField = mergedFields.find(
              (f: any) => f.dataType === 'date' || f.dataType === 'timestamp',
            );

            if (dateField) {
              if (dateField.timeGrain === 'date') {
                newChartAxes[1].fields = [dateField];
              } else {
                callAPI = true;
                newChartAxes[1].fields = [
                  {
                    dataType: dateField.dataType,
                    displayname: 'date of ' + dateField.fieldname,
                    fieldname: dateField.fieldname,
                    tableId: dateField.tableId,
                    uId: dateField.uId,
                    timeGrain: 'date',
                  },
                ];
              }

              if (mergedFields.length > 1) {
                toast.warning('Extra fields are removed', {
                  position: 'top-center',
                  autoClose: 10000,
                  toastId: 'chart-data-limit-warning',
                  style: { top: '10px' },
                });
              }
            } else {
              toast.warning('Non-compatible fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-data-limit-warning',
                style: { top: '10px' },
              });

              newChartAxes[1].fields = [];
              callAPI = true;
            }
          }

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[2].fields = assignWithLimitCheck(newChart, 2, oldChartAxes[3].fields);
          }

          if (
            oldChartAxes[2].fields.length > 0 ||
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[1].fields.length !== oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'bubbleMap') {
          keepOldData(propKey, false);

          // Axis 0
          if (oldChartAxes[0]?.fields?.length > 0) {
            newChartAxes[0].fields = oldChartAxes[0].fields;
          }

          const mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];

          if (mergedFields.length === 1) {
            const field = mergedFields[0];

            if (field?.dataType === 'text') {
              newChartAxes[2].fields = [field];
            } else {
              toast.warning('Non-compatible fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-data-limit-warning',
                style: { top: '10px' },
              });
            }
          } else if (mergedFields.length > 1) {
            const textFields = mergedFields.filter((f: any) => f?.dataType === 'text');

            if (textFields.length > 0) {
              newChartAxes[2].fields = [textFields[0]]; // only first text
              const remainingFields =
                textFields.length > 0
                  ? mergedFields.filter((f: any) => f !== textFields[0])
                  : mergedFields;

              if (remainingFields.length > 0) {
                newChartAxes[1].fields = getFieldsToChartAllowedNumbers(
                  newChart,
                  1,
                  remainingFields,
                );
              }
            } else {
              toast.warning('Non-compatible fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-data-limit-warning',
                style: { top: '10px' },
              });
              newChartAxes[1].fields = [];
              newChartAxes[2].fields = [];
            }
          }

          // Axis 3
          if (oldChartAxes[3]?.fields?.length > 0) {
            newChartAxes[3].fields = assignWithLimitCheck(newChart, 3, oldChartAxes[3].fields);
          }

          let callAPI = measureModified;

          if (
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length !== oldChartAxes[2].fields.length ||
            newChartAxes[3].fields.length !== oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        break;
      case 'multibar':
      case 'stackedBar':
      case 'horizontalBar':
      case 'horizontalStacked':
        if (['multibar', 'stackedBar', 'horizontalBar', 'horizontalStacked'].includes(newChart)) {
          keepOldData(propKey, false);
          return { newChartAxes: oldChartAxes, callAPI: measureModified };
        }

        if (['line', 'area', 'stackedArea'].includes(newChart)) {
          keepOldData(propKey, false);

          const mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];

          if (oldChartAxes[0].fields.length > 0) {
            newChartAxes[0].fields = oldChartAxes[0].fields;
          }

          if (mergedFields.length === 1) {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(newChart, 1, [mergedFields[0]]);
          }

          if (mergedFields.length > 1 && oldChartAxes[3].fields.length <= 1) {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(newChart, 1, [mergedFields[0]]);

            newChartAxes[2].fields = getFieldsToChartAllowedNumbers(newChart, 2, [mergedFields[1]]);
          }

          if (mergedFields.length > 1 && oldChartAxes[3].fields.length > 1) {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(newChart, 1, mergedFields);
          }

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[3].fields = assignWithLimitCheck(newChart, 3, oldChartAxes[3].fields);
          }

          const finalPlacedFields = [
            ...(newChartAxes[1]?.fields || []),
            ...(newChartAxes[2]?.fields || []),
          ];

          const removedFields = mergedFields.filter((field) => !finalPlacedFields.includes(field));

          if (removedFields.length > 0) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }

          let callAPI = measureModified;
          if (
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length !== oldChartAxes[2].fields.length ||
            newChartAxes[3].fields.length !== oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (['waterfall', 'treeMap', 'sankey', 'filledMap'].includes(newChart)) {
          keepOldData(propKey, false);

          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          const mergedFields: any[] = [];
          oldChartAxes[1].fields.forEach((f: any) => {
            mergedFields.push(f);
          });

          oldChartAxes[2].fields.forEach((f: any) => {
            mergedFields.push(f);
          });

          if (newChart === 'filledMap') {
            const textFields: any[] = [];

            mergedFields.forEach((f: any) => {
              if (f.dataType === 'text') {
                textFields.push(f);
              }
            });

            if (textFields.length < mergedFields.length) {
              toast.warning('Non-compatible fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-noncompatible-warning',
                style: { top: '10px' },
              });
            }

            const allowedFields = getFieldsToChartAllowedNumbers(newChart, 1, textFields);

            newChartAxes[1].fields = allowedFields;

            if (allowedFields.length < textFields.length) {
              toast.warning('Extra fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-noncompatible-warning',
                style: { top: '10px' },
              });
            }
          } else {
            newChartAxes[1].fields = assignWithLimitCheck(newChart, 1, mergedFields);
          }

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[2].fields = assignWithLimitCheck(newChart, 2, oldChartAxes[3].fields);
          }

          let callAPI = measureModified;
          if (
            oldChartAxes[2].fields.length > 0 ||
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length !== oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'scatterPlot') {
          keepOldData(propKey, false);

          const mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];

          const measures = oldChartAxes[3].fields;

          if (oldChartAxes[0].fields.length > 0) {
            newChartAxes[0].fields = oldChartAxes[0].fields;
          }

          if (mergedFields.length >= 1) {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(newChart, 1, mergedFields);
          }

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[2].fields = getFieldsToChartAllowedNumbers(
              newChart,
              2,
              oldChartAxes[3].fields,
            );

            if (oldChartAxes[3].fields.length > 1)
              newChartAxes[3].fields = [oldChartAxes[3].fields[1]];
          }

          const finalPlacedFields = [
            ...(newChartAxes[1]?.fields || []),
            ...(newChartAxes[2]?.fields || []),
          ];

          const removedFields = mergedFields.filter((field) => !finalPlacedFields.includes(field));

          if (removedFields.length > 0 || measures.length > 2) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }

          let callAPI = measureModified;
          if (
            oldChartAxes[2].fields.length > 0 ||
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length + newChartAxes[3].fields.length !==
              oldChartAxes[3].fields.length ||
            oldChartAxes[3].fields.length !== 2
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (
          newChart === 'pie' ||
          newChart === 'donut' ||
          newChart === 'rose' ||
          newChart === 'wordCloud'
        ) {
          keepOldData(propKey, false);

          const mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];

          if (oldChartAxes[0].fields.length > 0) {
            newChartAxes[0].fields = oldChartAxes[0].fields;
          }

          if (mergedFields.length) {
            newChartAxes[1].fields = assignWithLimitCheck(newChart, 1, mergedFields);
          }

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[2].fields = assignWithLimitCheck(newChart, 3, oldChartAxes[3].fields);
          }

          let callAPI = measureModified;
          if (
            oldChartAxes[2].fields.length > 0 ||
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length !== oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'table') {
          keepOldData(propKey, false);

          const mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];

          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          if (oldChartAxes[1].fields.length > 0) {
            newChartAxes[1].fields = oldChartAxes[1].fields;
          }

          if (mergedFields) {
            newChartAxes[1].fields = assignWithLimitCheck(newChart, 2, mergedFields);
          }

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[2].fields = assignWithLimitCheck(newChart, 2, oldChartAxes[3].fields);
          }

          let callAPI = measureModified;
          if (
            oldChartAxes[2].fields.length > 0 ||
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length !== oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'heatmap' || newChart === 'boxPlot') {
          keepOldData(propKey, false);

          const mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          if (mergedFields.length === 1) {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(newChart, 1, [mergedFields[0]]);
          }

          if (mergedFields.length > 1) {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(newChart, 1, [mergedFields[0]]);

            newChartAxes[2].fields = getFieldsToChartAllowedNumbers(newChart, 2, [mergedFields[1]]);
          }

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[3].fields = assignWithLimitCheck(newChart, 3, oldChartAxes[3].fields);
          }

          const finalPlacedFields = [
            ...(newChartAxes[1]?.fields || []),
            ...(newChartAxes[2]?.fields || []),
          ];

          const removedFields = mergedFields.filter((field) => !finalPlacedFields.includes(field));

          if (removedFields.length > 0) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }

          let callAPI = measureModified;
          if (
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length !== oldChartAxes[2].fields.length ||
            newChartAxes[3].fields.length !== oldChartAxes[3].fields.length ||
            newChartAxes[2].fields.length === 0
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'crossTab') {
          keepOldData(propKey, false);

          let mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];

          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          if (mergedFields) {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(newChart, 1, mergedFields);
          }

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[3].fields = assignWithLimitCheck(newChart, 3, oldChartAxes[3].fields);
          }

          let callAPI = measureModified;
          if (
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length !== oldChartAxes[2].fields.length ||
            newChartAxes[3].fields.length !== oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (['gauge', 'funnel', 'simplecard', 'richText'].includes(newChart)) {
          keepOldData(propKey, false);
          const mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];

          if (mergedFields.length > 0) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }

          if (oldChartAxes[0].fields.length > 0) {
            newChartAxes[0].fields = oldChartAxes[0].fields;
          }

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[1].fields = assignWithLimitCheck(newChart, 1, oldChartAxes[3].fields);
          }

          let callAPI = measureModified;
          if (
            oldChartAxes[1].fields.length > 0 ||
            oldChartAxes[2].fields.length > 0 ||
            newChartAxes[1].fields.length !== oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'calendar') {
          keepOldData(propKey, false);

          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          let callAPI = measureModified;

          const mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];

          if (mergedFields.length > 0) {
            const dateField = mergedFields.find(
              (f: any) => f.dataType === 'date' || f.dataType === 'timestamp',
            );

            if (dateField) {
              if (dateField.timeGrain === 'date') {
                newChartAxes[1].fields = [dateField];
              } else {
                callAPI = true;

                newChartAxes[1].fields = [
                  {
                    dataType: dateField.dataType,
                    displayname: 'date of ' + dateField.fieldname,
                    fieldname: dateField.fieldname,
                    tableId: dateField.tableId,
                    uId: dateField.uId,
                    timeGrain: 'date',
                  },
                ];
              }

              if (mergedFields.length > 1) {
                toast.warning('Extra fields are removed', {
                  position: 'top-center',
                  autoClose: 10000,
                  toastId: 'chart-data-limit-warning',
                  style: { top: '10px' },
                });
              }
            } else {
              toast.warning('Non-compatible fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-data-limit-warning',
                style: { top: '10px' },
              });

              newChartAxes[1].fields = [];
              callAPI = true;
            }
          }

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[2].fields = assignWithLimitCheck(newChart, 2, oldChartAxes[3].fields);
          }

          if (
            oldChartAxes[2].fields.length > 0 ||
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length !== oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'bubbleMap') {
          keepOldData(propKey, false);

          // Axis 0
          if (oldChartAxes[0]?.fields?.length > 0) {
            newChartAxes[0].fields = oldChartAxes[0].fields;
          }

          const mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];

          if (mergedFields.length === 1) {
            const field = mergedFields[0];

            if (field?.dataType === 'text') {
              newChartAxes[2].fields = [field];
            } else {
              toast.warning('Non-compatible fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-data-limit-warning',
                style: { top: '10px' },
              });
            }
          } else if (mergedFields.length > 1) {
            const textFields = mergedFields.filter((f: any) => f?.dataType === 'text');

            if (textFields.length > 0) {
              newChartAxes[2].fields = [textFields[0]]; // only first text

              const remainingFields = mergedFields.filter((f: any) => f !== textFields[0]);

              if (remainingFields.length > 0) {
                newChartAxes[1].fields = [remainingFields[0]];
              }
            } else {
              toast.warning('Non-compatible fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-data-limit-warning',
                style: { top: '10px' },
              });

              newChartAxes[1].fields = [];
              newChartAxes[2].fields = [];
            }
          }

          // Axis 3
          if (oldChartAxes[3]?.fields?.length > 0) {
            newChartAxes[3].fields = assignWithLimitCheck(newChart, 3, oldChartAxes[3].fields);
          }

          if (mergedFields.length > 2) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }

          let callAPI = measureModified;

          if (
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length !== oldChartAxes[2].fields.length ||
            newChartAxes[3].fields.length !== oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }
        break;

      case 'waterfall':
      case 'treeMap':
      case 'filledMap':
      case 'table':
      case 'sankey':
        if (['waterfall', 'treeMap', 'sankey'].includes(newChart)) {
          if (oldChart === 'filledMap') {
            if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(
              newChart,
              1,
              oldChartAxes[1].fields,
            );
            keepOldData(propKey, false);
            newChartAxes[2].fields = getFieldsToChartAllowedNumbers(
              newChart,
              2,
              oldChartAxes[2].fields,
            );
            UpdateChartDataWithNewChartAxes(oldChartAxes, newChartAxes, 2);
            newChartAxes[2].fields = newChartAxes[2].fields;
            keepOldData(propKey, false);
            let callAPI = measureModified;

            if (
              newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
              newChartAxes[2].fields.length !== oldChartAxes[2].fields.length
            ) {
              callAPI = true;
            }

            return { newChartAxes, callAPI };
          } else if (oldChart === 'table') {
            if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;
            newChartAxes[1].fields = assignWithLimitCheck(newChart, 1, oldChartAxes[1].fields);

            newChartAxes[2].fields = assignWithLimitCheck(newChart, 2, oldChartAxes[2].fields);

            //change agg and display name
            const allowedFieldNumber = getAllowedMeasureFields(newChart);
            const { newFields, callQueryAPI } = convertDateMinMaxToCount(
              oldChartAxes[2].fields,
              allowedFieldNumber,
            );
            newChartAxes[2].fields = [...newFields];

            let callAPI = measureModified;
            if (
              newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
              newChartAxes[2].fields.length !== oldChartAxes[2].fields.length ||
              callQueryAPI === true
            ) {
              callAPI = true;
            }

            return { newChartAxes, callAPI };
          } else {
            let callAPI = measureModified;

            if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;
            if (
              IsOldChartFieldsCountIsGreater(newChart, 1) ||
              IsOldChartFieldsCountIsGreater(newChart, 2)
            ) {
              callAPI = true;
              if (IsOldChartFieldsCountIsGreater(newChart, 1)) {
                oldChartAxes[1].fields = assignWithLimitCheck(newChart, 1, oldChartAxes[1].fields);

                keepOldData(propKey, false);
              }

              if (IsOldChartFieldsCountIsGreater(newChart, 2)) {
                newChartAxes[2].fields = assignWithLimitCheck(newChart, 2, oldChartAxes[2].fields);

                UpdateChartDataWithNewChartAxes(oldChartAxes, newChartAxes, 2);
                oldChartAxes[2].fields = newChartAxes[2].fields;
                keepOldData(propKey, false);
              }

              return { newChartAxes: oldChartAxes, callAPI };
            } else {
              keepOldData(propKey, false);
              return { newChartAxes: oldChartAxes, callAPI: measureModified };
            }
          }
        }

        if (
          [
            'line',
            'area',
            'stackedArea',
            'multibar',
            'stackedBar',
            'horizontalBar',
            'horizontalStacked',
          ].includes(newChart)
        ) {
          keepOldData(propKey, false);
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          // const mergedFields = [
          //   ...(oldChartAxes[1]?.fields || []),
          //   // ...(oldChartAxes[2]?.fields || []),
          // ];

          if (oldChartAxes[1].fields.length > 2) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-extra-warning',
              style: { top: '10px' },
            });
          }

          if (oldChartAxes[1]?.fields.length === 1) {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(newChart, 1, [
              oldChartAxes[1].fields[0],
            ]);
          }

          if (oldChartAxes[1]?.fields.length > 1 && oldChartAxes[2].fields.length < 2) {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(newChart, 1, [
              oldChartAxes[1].fields[0],
            ]);
            newChartAxes[2].fields = getFieldsToChartAllowedNumbers(newChart, 2, [
              oldChartAxes[1].fields[1],
            ]);
          } else {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(
              newChart,
              1,
              oldChartAxes[1].fields,
            );
            if (
              (newChart === 'line' || newChart === 'area' || newChart === 'stackedArea') &&
              oldChartAxes[1].fields.length > 1
            ) {
              toast.warning('Extra fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-extra-warning',
                style: { top: '10px' },
              });
            }
          }

          if (oldChartAxes[2].fields.length > 0) {
            newChartAxes[3].fields = getFieldsToChartAllowedNumbers(
              newChart,
              3,
              oldChartAxes[2].fields,
            );
          }
          let callAPI = measureModified;

          // Change min/max agg with date to count
          const allowedFieldNumber = getAllowedMeasureFields(newChart);
          const { newFields, callQueryAPI } = convertDateMinMaxToCount(
            newChartAxes[3].fields,
            allowedFieldNumber,
          );
          newChartAxes[3].fields = [...newFields];

          if (
            callQueryAPI ||
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[3].fields.length !== oldChartAxes[2].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'filledMap') {
          keepOldData(propKey, false);

          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          if (oldChartAxes[1].fields.length > 0) {
            const textFields = oldChartAxes[1].fields.filter((f: any) => f.dataType === 'text');

            if (textFields.length !== oldChartAxes[1].fields.length) {
              toast.warning('Non-compatible fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-data-limit-warning',
                style: { top: '10px' },
              });
            } else {
              if (oldChartAxes[1].fields.length > 1) {
                toast.warning('Extra fields are removed', {
                  position: 'top-center',
                  autoClose: 10000,
                  toastId: 'chart-extra-warning',
                  style: { top: '10px' },
                });
              }
            }
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(newChart, 1, textFields);
          }
          newChartAxes[2].fields = getFieldsToChartAllowedNumbers(
            newChart,
            2,
            oldChartAxes[2].fields,
          );
          let callAPI = measureModified;
          // Change min/max agg with date to count
          const allowedFieldNumber = getAllowedMeasureFields(newChart);
          const { newFields, callQueryAPI } = convertDateMinMaxToCount(
            newChartAxes[2].fields,
            allowedFieldNumber,
          );
          newChartAxes[2].fields = [...newFields];

          if (oldChart === 'table' && oldChartAxes[2].fields.length > 1) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-extra-warning',
              style: { top: '10px' },
            });
          }

          if (
            callQueryAPI ||
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length !== oldChartAxes[2].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'table') {
          keepOldData(propKey, false);

          if (oldChartAxes[1].fields.length > 0) {
            newChartAxes[1].fields = oldChartAxes[1].fields;
          }

          if (oldChartAxes[2].fields.length > 0) {
            newChartAxes[2].fields = oldChartAxes[2].fields;
          }

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          return { newChartAxes, callAPI: measureModified };
        }

        if (newChart === 'calendar') {
          if (oldChartAxes[1].fields.length > 0) {
            const dateField = oldChartAxes[1].fields.find(
              (f: any) => f.dataType === 'date' || f.dataType === 'timestamp',
            );
            let callAPI = measureModified;
            if (dateField) {
              keepOldData(propKey, false);

              newChartAxes[0].fields = oldChartAxes[0].fields;
              if (dateField.timeGrain === 'date') {
                newChartAxes[1].fields.push(dateField);
              } else {
                //In case of Calendar chart timegrain should be date
                updateChartData(propKey, '');
                callAPI = true;
                newChartAxes[1].fields = [
                  {
                    dataType: dateField.dataType,
                    displayname: 'date of ' + dateField.fieldname,
                    fieldname: dateField.fieldname,
                    tableId: dateField.tableId,
                    uId: dateField.uId,
                    timeGrain: 'date',
                  },
                ];
              }
              if (oldChartAxes[1].fields.length > 1) {
                toast.warning('Extra fields are removed', {
                  position: 'top-center',
                  autoClose: 10000,
                  toastId: 'chart-data-limit-warning',
                  style: { top: '10px' },
                });
                callAPI = true;
              }
              if (oldChartAxes[2].fields.length > 0)
                newChartAxes[2].fields.push(oldChartAxes[2].fields[0]);

              if (oldChart === 'table') {
                //change axes and display name
                const allowedFieldNumber = getAllowedMeasureFields(newChart);
                const { newFields, callQueryAPI } = convertDateMinMaxToCount(
                  oldChartAxes[2].fields,
                  allowedFieldNumber,
                );
                if (callQueryAPI === true) {
                  callAPI = true;
                }
                newChartAxes[2].fields = [...newFields];
              }
              if (
                newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
                newChartAxes[2].fields.length !== oldChartAxes[2].fields.length
              ) {
                callAPI = true;
              }

              return { newChartAxes, callAPI };
            } else {
              toast.warning('Non-compatible fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-data-limit-warning',
                style: { top: '10px' },
              });
              keepOldData(propKey, false);
              callAPI = true;

              newChartAxes[0].fields = oldChartAxes[0].fields;
              newChartAxes[1].fields = [];
              if (oldChartAxes[2].fields.length > 0)
                newChartAxes[2].fields.push(oldChartAxes[2].fields[0]);
              if (oldChart === 'table') {
                //change axes and display name
                const allowedFieldNumber = getAllowedMeasureFields(newChart);
                const { newFields, callQueryAPI } = convertDateMinMaxToCount(
                  oldChartAxes[2].fields,
                  allowedFieldNumber,
                );
                newChartAxes[2].fields = [...newFields];
              }

              return { newChartAxes, callAPI };
            }
          } else {
            keepOldData(propKey, false);
            updateChartData(propKey, '');

            let callAPI = measureModified;
            newChartAxes[0].fields = oldChartAxes[0].fields;
            newChartAxes[1].fields = [];
            if (oldChartAxes[2].fields.length > 0)
              newChartAxes[2].fields.push(oldChartAxes[2].fields[0]);

            if (oldChartAxes[2].fields.length > 1) {
              toast.warning('Extra fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-extra-warning',
                style: { top: '10px' },
              });
            }

            if (oldChart === 'table') {
              //change axes and display name
              const allowedFieldNumber = getAllowedMeasureFields(newChart);
              const { newFields, callQueryAPI } = convertDateMinMaxToCount(
                oldChartAxes[2].fields,
                allowedFieldNumber,
              );
              newChartAxes[2].fields = [...newFields];
              if (callQueryAPI === true) {
                callAPI = true;
              }
            }
            if (newChartAxes[2].fields.length !== oldChartAxes[2].fields.length) {
              callAPI = true;
            }
            return { newChartAxes, callAPI };
          }
        }

        if (
          newChart === 'pie' ||
          newChart === 'donut' ||
          newChart === 'rose' ||
          newChart === 'wordCloud'
        ) {
          keepOldData(propKey, false);

          newChartAxes[0].fields = oldChartAxes[0].fields; //Filter

          if (oldChartAxes[1].fields.length > 0)
            // newChartAxes[1].fields.push(oldChartAxes[1].fields[0]); //Dimension	allowedNumbers: 1,
            newChartAxes[1].fields = assignWithLimitCheck(newChart, 1, oldChartAxes[1].fields);

          newChartAxes[2].fields = assignWithLimitCheck(newChart, 2, oldChartAxes[2].fields); //Measure

          let callAPI = measureModified;
          if (oldChart === 'table') {
            //change agg and display name
            const allowedFieldNumber = getAllowedMeasureFields(newChart);
            const { newFields, callQueryAPI } = convertDateMinMaxToCount(
              oldChartAxes[2].fields,
              allowedFieldNumber,
            );
            newChartAxes[2].fields = [...newFields];
            if (callQueryAPI === true) {
              callAPI = true;
            }
          }
          if (
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length !== oldChartAxes[2].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'scatterPlot') {
          let callAPI = measureModified;

          if (oldChartAxes[2].fields.length > 2 || oldChartAxes[1].fields.length > 1) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }

          keepOldData(propKey, false);

          // Map Category to Category
          if (oldChartAxes[1].fields.length > 0)
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(
              newChart,
              1,
              oldChartAxes[1].fields,
            );

          // Map Value to X and Y columns if there are more than one values
          if (oldChartAxes[2].fields.length > 0) {
            if (oldChartAxes[2].fields.length > 1) {
              newChartAxes[2].fields.push(oldChartAxes[2].fields.shift());
              newChartAxes[3].fields.push(oldChartAxes[2].fields.shift());

              if (oldChart === 'table') {
                //change agg and display name x and y axis
                let xAxisNames: string[] = [];
                if (newChartAxes[2]?.fields?.length > 0) {
                  const { newFields, callQueryAPI, notAllowedNames } = convertDateMinMaxToCount(
                    newChartAxes[2].fields,
                    1,
                  );
                  newChartAxes[2].fields = [...newFields];
                  xAxisNames = notAllowedNames;
                  if (callQueryAPI === true) {
                    callAPI = true;
                  }
                }
                if (newChartAxes[3]?.fields?.length > 0) {
                  const { newFields, callQueryAPI } = convertDateMinMaxToCount(
                    newChartAxes[3].fields,
                    2,
                    xAxisNames,
                  );
                  newChartAxes[3].fields = [...newFields];
                  if (callQueryAPI === true) {
                    callAPI = true;
                  }
                }
                ['labelFormats', 'tooltipFormats'].map((formatName) => {
                  const formatObject = chartControls.properties[propKey].formatOptions[formatName];
                  dispatch(
                    updateFormatOption(propKey, formatName, 'measureFormats', {
                      ...formatObject.measureFormats,
                      [newChartAxes[2].fields[0].uId]: {
                        ...formatObject.measureFormats[newChartAxes[2].fields[0].uId],
                        roundingDigits: 0,
                      },
                      [newChartAxes[3].fields[0].uId]: {
                        ...formatObject.measureFormats[newChartAxes[3].fields[0].uId],
                        roundingDigits: 0,
                      },
                    }),
                  );
                });
              }
            } else {
              newChartAxes[2].fields = oldChartAxes[2].fields;
              //change agg and display name of x axis
              if (oldChart === 'table') {
                const { newFields, callQueryAPI } = convertDateMinMaxToCount(
                  newChartAxes[2].fields,
                  1,
                );
                newChartAxes[2].fields = [...newFields];
                ['labelFormats', 'tooltipFormats'].map((formatName) => {
                  const formatObject = chartControls.properties[propKey].formatOptions[formatName];
                  dispatch(
                    updateFormatOption(propKey, formatName, 'measureFormats', {
                      ...formatObject.measureFormats,
                      [newChartAxes[2].fields[0].uId]: {
                        ...formatObject.measureFormats[newChartAxes[2].fields[0].uId],
                        roundingDigits: 0,
                      },
                    }),
                  );
                });
                if (callQueryAPI === true) {
                  callAPI = true;
                }
              }
            }
          }

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          if (
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length + newChartAxes[3].fields.length !==
              oldChartAxes[2].fields.length ||
            oldChartAxes[2].fields.length !== 2
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'funnel' || newChart === 'simplecard') {
          let callAPI = measureModified;
          //name: "Measure", allowedNumbers: 12,
          keepOldData(propKey, false);

          if (
            oldChartAxes[1]?.fields.length > 0 ||
            (oldChartAxes[2]?.fields.length > 1 && newChart !== 'funnel')
          ) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }

          if (oldChartAxes[2].fields.length > 0) {
            // newChartAxes[1].fields = oldChartAxes[2].fields; // this will work
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(
              newChart,
              1,
              oldChartAxes[2].fields,
            );

            if (oldChart === 'table') {
              //change agg and display name
              if (newChart === 'simplecard') {
                const allowedFieldNumber = getAllowedMeasureFields(newChart);
                const { newFields, callQueryAPI } = convertDateMinMaxToCount(
                  oldChartAxes[2].fields,
                  allowedFieldNumber,
                );
                if (callQueryAPI === true) {
                  callAPI = true;
                }
                newChartAxes[1].fields = [...newFields];
              } else if (newChart === 'funnel') {
                const allowedFieldNumber = getAllowedMeasureFields(newChart);
                const { newFields, callQueryAPI } = convertDateMinMaxToCount(
                  oldChartAxes[2].fields,
                  allowedFieldNumber,
                );
                if (callQueryAPI === true) {
                  callAPI = true;
                }
                newChartAxes[1].fields = [...newFields];
              }
            }
          }

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          if (
            oldChartAxes[1].fields.length > 0 ||
            newChartAxes[1].fields.length !== oldChartAxes[2].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'gauge' || newChart === 'richText') {
          let callAPI = measureModified;
          keepOldData(propKey, false);

          const mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];

          if (oldChartAxes[2]?.fields.length > 1 || oldChartAxes[1].fields.length > 0) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }
          if (oldChartAxes[2].fields.length > 0) {
            newChartAxes[1].fields.push(oldChartAxes[2].fields[0]);

            if (oldChart === 'table') {
              //change agg and display name
              const allowedFieldNumber = getAllowedMeasureFields(newChart);
              const { newFields, callQueryAPI } = convertDateMinMaxToCount(
                oldChartAxes[2].fields,
                allowedFieldNumber,
              );
              if (callQueryAPI === true) {
                callAPI = true;
              }
              newChartAxes[1].fields = [...newFields];
            }
          }

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          if (
            oldChartAxes[1].fields.length > 0 ||
            newChartAxes[1].fields.length !== oldChartAxes[2].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'heatmap' || newChart === 'boxPlot') {
          let callAPI = measureModified;
          keepOldData(propKey, false);

          if (oldChartAxes[1].fields.length > 0) {
            if (oldChartAxes[1].fields.length > 1) {
              newChartAxes[1].fields.push(oldChartAxes[1].fields[0]);
              newChartAxes[2].fields.push(oldChartAxes[1].fields[1]);
            } else {
              newChartAxes[1].fields = oldChartAxes[1].fields;
            }
          }

          if (oldChartAxes[2].fields.length > 0) {
            newChartAxes[3].fields.push(oldChartAxes[2].fields[0]);

            if (oldChart === 'table') {
              //change agg and display name
              const allowedFieldNumber = getAllowedMeasureFields(newChart);
              const { newFields, callQueryAPI } = convertDateMinMaxToCount(
                oldChartAxes[2].fields,
                allowedFieldNumber,
              );
              if (callQueryAPI === true) {
                callAPI = true;
              }
              newChartAxes[3].fields = [...newFields];
            }
          }

          if (oldChartAxes[2]?.fields.length > 1 || oldChartAxes[1]?.fields.length > 2) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          if (
            newChartAxes[1].fields.length + newChartAxes[2].fields.length !==
              oldChartAxes[1].fields.length ||
            newChartAxes[3].fields.length !== oldChartAxes[2].fields.length ||
            newChartAxes[2].fields.length === 0
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'crossTab') {
          keepOldData(propKey, false);
          if (oldChartAxes[1].fields.length > 0) {
            newChartAxes[1].fields = oldChartAxes[1].fields;
          }

          if (oldChartAxes[2].fields.length > 0) {
            newChartAxes[3].fields = oldChartAxes[2].fields;
          }

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          return { newChartAxes, callAPI: measureModified };
        }

        if (newChart === 'bubbleMap') {
          let callAPI = measureModified;
          keepOldData(propKey, false);

          if (oldChartAxes[1].fields.length > 0) {
            const fields = oldChartAxes[1].fields;

            const textField = fields.find((f: any) => f.dataType === 'text');

            // Only one field dropped
            if (fields.length === 1) {
              if (textField) {
                newChartAxes[2].fields.push(textField);
              } else {
                toast.warning('Non-compatible fields are removed', {
                  position: 'top-center',
                  autoClose: 10000,
                  toastId: 'chart-data-limit-warning',
                  style: { top: '10px' },
                });
              }
            }

            // More than one field dropped
            if (fields.length > 1) {
              if (textField) {
                // Text must go to axis [2]
                newChartAxes[2].fields.push(textField);

                // Any other field (any datatype) can go to axis [1]
                const otherField = fields.find((f: any) => f !== textField);
                if (otherField) {
                  newChartAxes[1].fields.push(otherField);
                }
                if (fields.length > 2) {
                  toast.warning('Extra fields are removed', {
                    position: 'top-center',
                    autoClose: 10000,
                    toastId: 'chart-data-limit-warning',
                    style: { top: '10px' },
                  });
                }
              } else {
                // No text field found → nothing valid for axis [2]
                toast.warning('Non-compatible fields are removed', {
                  position: 'top-center',
                  autoClose: 10000,
                  toastId: 'chart-data-limit-warning',
                  style: { top: '10px' },
                });
              }
            }
          }

          if (oldChartAxes[2].fields.length > 0) {
            newChartAxes[3].fields.push(oldChartAxes[2].fields[0]);

            if (oldChartAxes[2]?.fields.length > 1) {
              toast.warning('Extra fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-data-limit-warning',
                style: { top: '10px' },
              });
            }

            if (oldChart === 'table') {
              const allowedFieldNumber = getAllowedMeasureFields(newChart);
              const { newFields, callQueryAPI } = convertDateMinMaxToCount(
                oldChartAxes[2].fields,
                allowedFieldNumber,
              );
              if (callQueryAPI === true) {
                callAPI = true;
              }
              newChartAxes[3].fields = [...newFields];
            }
          }

          if (oldChartAxes[0].fields.length > 0) {
            newChartAxes[0].fields = oldChartAxes[0].fields;
          }

          if (
            newChartAxes[1].fields.length + newChartAxes[2].fields.length !==
              oldChartAxes[1].fields.length ||
            newChartAxes[3].fields.length !== oldChartAxes[2].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        break;

      case 'calendar':
        if (newChart === 'calendar') {
          return { newChartAxes: oldChartAxes, callAPI: measureModified };
        }

        if (
          [
            'line',
            'area',
            'stackedArea',
            'multibar',
            'stackedBar',
            'horizontalBar',
            'horizontalStacked',
          ].includes(newChart)
        ) {
          keepOldData(propKey, false);
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          if (oldChartAxes[1].fields.length > 0) {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(
              newChart,
              1,
              oldChartAxes[1].fields,
            );
          }

          if (oldChartAxes[2].fields.length > 0) {
            newChartAxes[3].fields = assignWithLimitCheck(newChart, 3, oldChartAxes[2].fields);
          }

          let callAPI = measureModified;
          if (
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[3].fields.length !== oldChartAxes[2].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (['waterfall', 'treeMap', 'sankey', 'table'].includes(newChart)) {
          keepOldData(propKey, false);

          if (oldChartAxes[1].fields.length > 0) {
            newChartAxes[1].fields = oldChartAxes[1].fields;
          }

          if (oldChartAxes[2].fields.length > 0) {
            newChartAxes[2].fields = oldChartAxes[2].fields;
          }

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          return { newChartAxes, callAPI: measureModified };
        }

        if (
          newChart === 'pie' ||
          newChart === 'donut' ||
          newChart === 'rose' ||
          newChart === 'wordCloud'
        ) {
          keepOldData(propKey, false);

          newChartAxes[0].fields = oldChartAxes[0].fields; //Filter

          if (oldChartAxes[1].fields.length > 0)
            newChartAxes[1].fields.push(oldChartAxes[1].fields[0]); //Dimension	allowedNumbers: 1,

          newChartAxes[2].fields = getFieldsToChartAllowedNumbers(
            newChart,
            2,
            oldChartAxes[2].fields,
          ); //Measure

          let callAPI = measureModified;
          if (
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length !== oldChartAxes[2].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'scatterPlot') {
          keepOldData(propKey, false);

          // Map Category to Category
          if (oldChartAxes[1].fields.length > 0)
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(
              newChart,
              1,
              oldChartAxes[1].fields,
            );

          // Map Value to X and Y columns if there are more than one values
          if (oldChartAxes[2].fields.length > 0) {
            if (oldChartAxes[2].fields.length > 1) {
              newChartAxes[2].fields.push(oldChartAxes[2].fields.shift());
              newChartAxes[3].fields.push(oldChartAxes[2].fields.shift());
            } else {
              newChartAxes[2].fields = oldChartAxes[2].fields;
            }
          }

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          let callAPI = measureModified;
          if (
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length + newChartAxes[3].fields.length !==
              oldChartAxes[2].fields.length ||
            oldChartAxes[2].fields.length !== 2
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'funnel' || newChart === 'simplecard') {
          keepOldData(propKey, false);

          if (oldChartAxes[2].fields.length > 0) newChartAxes[1].fields = oldChartAxes[2].fields;
          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          let callAPI = measureModified;
          if (
            oldChartAxes[1].fields.length > 0 ||
            newChartAxes[1].fields.length !== oldChartAxes[2].fields.length
          ) {
            callAPI = true;
          }

          if (oldChartAxes[1].fields.length >= 1) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'gauge' || newChart === 'richText') {
          keepOldData(propKey, false);
          if (oldChartAxes[2].fields.length > 0)
            newChartAxes[1].fields.push(oldChartAxes[2].fields[0]);

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          if (oldChartAxes[1].fields.length >= 1) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }

          let callAPI = measureModified;
          if (
            oldChartAxes[1].fields.length > 0 ||
            newChartAxes[1].fields.length !== oldChartAxes[2].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'heatmap' || newChart === 'boxPlot' || newChart === 'crossTab') {
          keepOldData(propKey, false);
          if (oldChartAxes[1].fields.length > 0) {
            newChartAxes[1].fields.push(oldChartAxes[1].fields[0]);
          }

          if (oldChartAxes[2].fields.length > 0) {
            newChartAxes[3].fields.push(oldChartAxes[2].fields[0]);
          }

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          let callAPI = measureModified;
          if (
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[3].fields.length !== oldChartAxes[2].fields.length
          ) {
            callAPI = true;
          }
          if (newChart !== 'crossTab' && newChartAxes[2].fields.length === 0) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'filledMap') {
          keepOldData(propKey, false);

          const textOnlyFields = oldChartAxes[1].fields.filter((f: any) => f.dataType === 'text');

          if (textOnlyFields.length !== oldChartAxes[1].fields.length) {
            toast.warning('Non-compatible fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }

          if (oldChartAxes[2].fields.length > 0) {
            newChartAxes[2].fields = oldChartAxes[2].fields;
          }

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          let callAPI = measureModified;
          if (oldChartAxes[1].fields.length > 0) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'bubbleMap') {
          keepOldData(propKey, false);

          if (oldChartAxes[1].fields.length > 0) {
            const textOnlyFields = oldChartAxes[1].fields.filter((f: any) => f.dataType === 'text');

            if (textOnlyFields.length !== oldChartAxes[1].fields.length) {
              toast.warning('Non-compatible fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-data-limit-warning',
                style: { top: '10px' },
              });
            }

            newChartAxes[2].fields = textOnlyFields;
          }

          if (oldChartAxes[2].fields.length > 0) {
            newChartAxes[3].fields = oldChartAxes[2].fields;
          }

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          let callAPI = measureModified;
          if (oldChartAxes[1].fields.length > 0) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }
        break;

      case 'pie':
      case 'donut':
      case 'rose':
      case 'wordCloud':
        if (
          ['waterfall', 'pie', 'donut', 'rose', 'treeMap', 'sankey', 'wordCloud'].includes(newChart)
        ) {
          keepOldData(propKey, false);
          return { newChartAxes: oldChartAxes, callAPI: measureModified };
        }

        if (
          [
            'line',
            'area',
            'stackedArea',
            'multibar',
            'stackedBar',
            'horizontalBar',
            'horizontalStacked',
          ].includes(newChart)
        ) {
          keepOldData(propKey, false);

          if (oldChartAxes[0].fields.length > 0) {
            newChartAxes[0].fields = oldChartAxes[0].fields;
          }

          if (oldChartAxes[1].fields.length > 0) {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(
              newChart,
              1,
              oldChartAxes[1].fields,
            );
          }

          if (oldChartAxes[2].fields.length > 0) {
            newChartAxes[3].fields = assignWithLimitCheck(newChart, 3, oldChartAxes[2].fields);
          }

          let callAPI = measureModified;
          if (
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[3].fields.length !== oldChartAxes[2].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'table' || newChart === 'filledMap') {
          keepOldData(propKey, false);

          if (oldChartAxes[1].fields.length > 0) {
            if (newChart === 'filledMap') {
              const textFields = oldChartAxes[1].fields.filter((f: any) => f.dataType === 'text');

              if (textFields.length !== oldChartAxes[1].fields.length) {
                toast.warning('Non-compatible fields are removed', {
                  position: 'top-center',
                  autoClose: 10000,
                  toastId: 'chart-data-limit-warning',
                  style: { top: '10px' },
                });
              }

              newChartAxes[1].fields = textFields;
            } else {
              newChartAxes[1].fields = oldChartAxes[1].fields;
            }
          }

          if (oldChartAxes[2].fields.length > 0) {
            newChartAxes[2].fields = oldChartAxes[2].fields;
          }

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          return { newChartAxes, callAPI: measureModified };
        }

        if (newChart === 'calendar') {
          if (oldChartAxes[1].fields.length > 0) {
            let callAPI = measureModified;
            if (
              oldChartAxes[1].fields[0].dataType === 'date' ||
              oldChartAxes[1].fields[0].dataType === 'timestamp'
            ) {
              keepOldData(propKey, false);

              newChartAxes[0].fields = oldChartAxes[0].fields;
              if (oldChartAxes[1].fields[0].timeGrain === 'date') {
                newChartAxes[1].fields = oldChartAxes[1].fields;
              } else {
                //In case of Calendar chart timegrain should be date
                updateChartData(propKey, '');
                callAPI = true;
                newChartAxes[1].fields = [
                  {
                    dataType: oldChartAxes[1].fields[0].dataType,
                    displayname: 'date of ' + oldChartAxes[1].fields[0].fieldname, // timegrain changed to date so change display name
                    fieldname: oldChartAxes[1].fields[0].fieldname,
                    tableId: oldChartAxes[1].fields[0].tableId,
                    uId: oldChartAxes[1].fields[0].uId,
                    timeGrain: 'date',
                  },
                ];
              }
              if (oldChartAxes[1].fields.length > 1) {
                toast.warning('Extra fields are removed', {
                  position: 'top-center',
                  autoClose: 10000,
                  toastId: 'chart-data-limit-warning',
                  style: { top: '10px' },
                });
                callAPI = true;
              }
              if (oldChartAxes[2].fields.length > 0)
                newChartAxes[2].fields = oldChartAxes[2].fields;
              return { newChartAxes, callAPI };
            } else {
              toast.warning('Non-compatible fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-data-limit-warning',
                style: { top: '10px' },
              });

              keepOldData(propKey, false);

              newChartAxes[0].fields = oldChartAxes[0].fields;
              newChartAxes[1].fields = [];
              if (oldChartAxes[2].fields.length > 0)
                newChartAxes[2].fields = oldChartAxes[2].fields;

              return { newChartAxes, callAPI: true };
            }
          } else {
            keepOldData(propKey, false);
            updateChartData(propKey, '');

            newChartAxes[0].fields = oldChartAxes[0].fields;
            newChartAxes[1].fields = [];
            if (oldChartAxes[2].fields.length > 0) newChartAxes[2].fields = oldChartAxes[2].fields;

            return { newChartAxes, callAPI: measureModified };
          }
        }

        if (newChart === 'scatterPlot') {
          keepOldData(propKey, false);

          // Map Category to Category
          if (oldChartAxes[1].fields.length > 0)
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(
              newChart,
              1,
              oldChartAxes[1].fields,
            );

          // Map Value to X and Y columns if there are more than one values
          if (oldChartAxes[2].fields.length > 0) {
            if (oldChartAxes[2].fields.length > 1) {
              newChartAxes[2].fields.push(oldChartAxes[2].fields.shift());
              newChartAxes[3].fields.push(oldChartAxes[2].fields.shift());
            } else {
              newChartAxes[2].fields = oldChartAxes[2].fields;
            }
          }

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          let callAPI = measureModified;
          if (
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length + newChartAxes[3].fields.length !==
              oldChartAxes[2].fields.length ||
            oldChartAxes[2].fields.length !== 2
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (
          newChart === 'gauge' ||
          newChart === 'richText' ||
          newChart === 'funnel' ||
          newChart === 'simplecard'
        ) {
          keepOldData(propKey, false);

          if (oldChartAxes[1].fields.length >= 1 || oldChartAxes[2].fields.length > 1) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }
          if (oldChartAxes[2].fields.length > 0)
            newChartAxes[1].fields.push(oldChartAxes[2].fields[0]);

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          let callAPI = measureModified;
          if (
            oldChartAxes[1].fields.length > 0 ||
            newChartAxes[1].fields.length !== oldChartAxes[2].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'crossTab' || newChart === 'boxPlot' || newChart === 'heatmap') {
          keepOldData(propKey, false);
          if (oldChartAxes[1].fields.length > 0) {
            newChartAxes[1].fields.push(oldChartAxes[1].fields[0]);
          }

          if (oldChartAxes[2].fields.length > 0) {
            newChartAxes[3].fields.push(oldChartAxes[2].fields[0]);
          }

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;
          let callAPI = measureModified;
          if (newChart !== 'crossTab') {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'bubbleMap') {
          keepOldData(propKey, false);
          if (oldChartAxes[1].fields.length > 0) {
            const textOnlyFields = oldChartAxes[1].fields.filter((f: any) => f.dataType === 'text');

            if (textOnlyFields.length !== oldChartAxes[1].fields.length) {
              toast.warning('Non-compatible fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-data-limit-warning',
                style: { top: '10px' },
              });
            }

            newChartAxes[2].fields = textOnlyFields;
          }

          if (oldChartAxes[2].fields.length > 0) {
            newChartAxes[3].fields = oldChartAxes[2].fields;
          }

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          return { newChartAxes, callAPI: measureModified };
        }
        break;

      case 'scatterPlot':
        if (newChart === 'scatterPlot') {
          return { newChartAxes: oldChartAxes, callAPI: measureModified };
        }

        if (
          [
            'waterfall',
            'pie',
            'table',
            'donut',
            'rose',
            'treeMap',
            'filledMap',
            'sankey',
            'wordCloud',
          ].includes(newChart)
        ) {
          keepOldData(propKey, false);

          const mergedFields = [
            ...(oldChartAxes[2]?.fields || []),
            ...(oldChartAxes[3]?.fields || []),
          ];

          // Map Category to Category
          if (newChart === 'filledMap' && oldChartAxes[1].fields.length > 0) {
            const textFields = oldChartAxes[1].fields.filter((f: any) => f.dataType === 'text');

            if (newChart === 'filledMap' && textFields.length !== oldChartAxes[1].fields.length) {
              toast.warning('Non-compatible fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-data-limit-warning',
                style: { top: '10px' },
              });
            }

            newChartAxes[1].fields = textFields;
          } else {
            newChartAxes[1].fields = oldChartAxes[1].fields;
          }

          // Map X & Y to Value
          newChartAxes[2].fields = getFieldsToChartAllowedNumbers(
            newChart,
            2,
            oldChartAxes[2].fields,
            oldChartAxes[3].fields,
          );

          if (mergedFields.length > 1 && newChart !== 'table') {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-extra-warning',
              style: { top: '10px' },
            });
          }

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;
          let callAPI = measureModified;
          if (
            newChartAxes[2].fields.length !==
            oldChartAxes[2].fields.length + oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (
          [
            'line',
            'area',
            'stackedArea',
            'multibar',
            'stackedBar',
            'horizontalBar',
            'horizontalStacked',
          ].includes(newChart)
        ) {
          keepOldData(propKey, false);
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          if (oldChartAxes[1].fields.length > 0) {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(
              newChart,
              1,
              oldChartAxes[1].fields,
            );
          }

          if (oldChartAxes[2].fields.length > 0 || oldChartAxes[3].fields.length > 0) {
            newChartAxes[3].fields = assignWithLimitCheck(
              newChart,
              3,
              oldChartAxes[2].fields,
              oldChartAxes[3].fields,
            );
          }

          let callAPI = measureModified;
          if (
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[3].fields.length !==
              oldChartAxes[2].fields.length + oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'calendar') {
          const mergedFields = [
            ...(oldChartAxes[2]?.fields || []),
            ...(oldChartAxes[3]?.fields || []),
          ];

          if (oldChartAxes[1].fields.length > 0) {
            let callAPI = measureModified;
            if (
              oldChartAxes[1].fields[0].dataType === 'date' ||
              oldChartAxes[1].fields[0].dataType === 'timestamp'
            ) {
              keepOldData(propKey, false);
              updateChartData(propKey, '');

              newChartAxes[0].fields = oldChartAxes[0].fields;
              if (oldChartAxes[1].fields[0].timeGrain === 'date') {
                newChartAxes[1].fields = oldChartAxes[1].fields;
              } else {
                //In case of Calendar chart timegrain should be date
                callAPI = true;
                newChartAxes[1].fields = [
                  {
                    dataType: oldChartAxes[1].fields[0].dataType,
                    displayname: 'date of ' + oldChartAxes[1].fields[0].fieldname, // timegrain changed to date so change display name
                    fieldname: oldChartAxes[1].fields[0].fieldname,
                    tableId: oldChartAxes[1].fields[0].tableId,
                    uId: oldChartAxes[1].fields[0].uId,
                    timeGrain: 'date',
                  },
                ];
              }
              if (oldChartAxes[3].fields.length > 0)
                newChartAxes[2].fields = oldChartAxes[3].fields;
              else if (oldChartAxes[2].fields.length > 0)
                newChartAxes[2].fields.push(oldChartAxes[2].fields[0]);
              if (
                newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
                newChartAxes[2].fields.length !==
                  oldChartAxes[2].fields.length + oldChartAxes[3].fields.length
              ) {
                callAPI = true;
              }

              return { newChartAxes, callAPI };
            } else {
              toast.warning('Non-compatible fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-data-limit-warning',
                style: { top: '10px' },
              });
              keepOldData(propKey, false);

              newChartAxes[1].fields = [];
              if (oldChartAxes[3].fields.length > 0)
                newChartAxes[2].fields.push(oldChartAxes[3].fields[0]);
              else if (oldChartAxes[2].fields.length > 0)
                newChartAxes[2].fields.push(oldChartAxes[2].fields[0]);

              return { newChartAxes, callAPI: true };
            }
          } else {
            keepOldData(propKey, false);
            updateChartData(propKey, '');

            newChartAxes[0].fields = oldChartAxes[0].fields;
            newChartAxes[1].fields = oldChartAxes[1].fields;
            if (oldChartAxes[3].fields.length > 0)
              newChartAxes[2].fields.push(oldChartAxes[3].fields[0]);
            else if (oldChartAxes[2].fields.length > 0)
              newChartAxes[2].fields.push(oldChartAxes[2].fields[0]);

            let callAPI = measureModified;

            if (
              newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
              newChartAxes[2].fields.length !==
                oldChartAxes[2].fields.length + oldChartAxes[3].fields.length
            ) {
              callAPI = true;
            }

            if (mergedFields.length > 1) {
              toast.warning('Extra fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-extra-warning',
                style: { top: '10px' },
              });
            }

            return { newChartAxes, callAPI };
          }
        }

        if (
          newChart === 'funnel' ||
          newChart === 'simplecard' ||
          newChart === 'gauge' ||
          newChart === 'richText'
        ) {
          const mergedFields = [
            ...(oldChartAxes[2]?.fields || []),
            ...(oldChartAxes[3]?.fields || []),
          ];

          if (oldChartAxes[1]?.fields.length >= 1) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }
          keepOldData(propKey, false);
          newChartAxes[1].fields = getFieldsToChartAllowedNumbers(
            newChart,
            1,
            oldChartAxes[2].fields,
            oldChartAxes[3].fields,
          );

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          if (mergedFields.length > 1 && newChart !== 'funnel') {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }

          let callAPI = measureModified;
          if (
            oldChartAxes[1].fields.length > 0 ||
            newChartAxes[1].fields.length !==
              oldChartAxes[2].fields.length + oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'heatmap' || newChart === 'crossTab' || newChart === 'boxPlot') {
          keepOldData(propKey, false);
          if (oldChartAxes[1].fields.length > 0)
            newChartAxes[1].fields.push(oldChartAxes[1].fields[0]);

          newChartAxes[3].fields = assignWithLimitCheck(
            newChart,
            3,
            oldChartAxes[2].fields,
            oldChartAxes[3].fields,
          );

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          let callAPI = measureModified;
          if (
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length !==
              oldChartAxes[2].fields.length + oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }
          if (newChart !== 'crossTab' && newChartAxes[2].fields.length === 0) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'bubbleMap') {
          keepOldData(propKey, false);
          const mergedFields = [
            ...(oldChartAxes[2]?.fields || []),
            ...(oldChartAxes[3]?.fields || []),
          ];

          if (oldChartAxes[1].fields.length > 0) {
            if (oldChartAxes[1].fields[0].dataType === 'text') {
              newChartAxes[2].fields.push(oldChartAxes[1].fields[0]);
            } else {
              toast.warning('Non-compatible fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-data-limit-warning',
                style: { top: '10px' },
              });
            }
          }

          if (oldChartAxes[2].fields.length > 0)
            newChartAxes[3].fields.push(oldChartAxes[2].fields[0]);
          else if (oldChartAxes[3].fields.length > 0)
            newChartAxes[3].fields.push(oldChartAxes[3].fields[0]);

          if (mergedFields.length > 1) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-extra-warning',
              style: { top: '10px' },
            });
          }

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          let callAPI = measureModified;
          if (
            newChartAxes[2].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[3].fields.length !==
              oldChartAxes[2].fields.length + oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        break;
      case 'funnel':
        if (newChart === 'funnel') {
          return { newChartAxes: oldChartAxes, callAPI: measureModified };
        }

        if (
          [
            'waterfall',
            'pie',
            'table',
            'donut',
            'rose',
            'calendar',
            'treeMap',
            'filledMap',
            'sankey',
            'wordCloud',
          ].includes(newChart)
        ) {
          keepOldData(propKey, false);
          if (oldChartAxes[1].fields.length > 0)
            newChartAxes[2].fields = getFieldsToChartAllowedNumbers(
              newChart,
              2,
              oldChartAxes[1].fields,
            );

          if (oldChartAxes[1].fields.length > 1 && newChart !== 'table') {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-extra-warning',
              style: { top: '10px' },
            });
          }

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          return { newChartAxes, callAPI: true };
        }

        if (newChart === 'scatterPlot') {
          keepOldData(propKey, false);
          if (oldChartAxes[1].fields.length > 2) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }
          if (oldChartAxes[1].fields.length > 0) {
            if (oldChartAxes[1].fields.length > 1) {
              newChartAxes[2].fields.push(oldChartAxes[1].fields.shift());
              newChartAxes[3].fields.push(oldChartAxes[1].fields.shift());
            } else newChartAxes[2].fields = oldChartAxes[1].fields;
          }

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          return { newChartAxes, callAPI: true };
        }

        if (newChart === 'gauge' || newChart === 'richText' || newChart === 'simplecard') {
          keepOldData(propKey, false);
          if (oldChartAxes[1].fields.length > 0)
            newChartAxes[1].fields.push(oldChartAxes[1].fields[0]);

          if (oldChartAxes[1].fields.length > 1) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-extra-warning',
              style: { top: '10px' },
            });
          }

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          let callAPI = measureModified;
          if (newChartAxes[1].fields.length !== oldChartAxes[1].fields.length) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (
          newChart === 'heatmap' ||
          newChart === 'crossTab' ||
          newChart === 'boxPlot' ||
          newChart === 'bubbleMap'
        ) {
          keepOldData(propKey, false);
          if (oldChartAxes[1].fields.length > 0)
            newChartAxes[3].fields = assignWithLimitCheck(newChart, 3, oldChartAxes[1].fields);

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          let callAPI = measureModified;
          if (newChartAxes[3].fields.length !== oldChartAxes[1].fields.length) {
            callAPI = true;
          }
          if (newChart !== 'crossTab') {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (
          [
            'line',
            'area',
            'stackedArea',
            'multibar',
            'stackedBar',
            'horizontalBar',
            'horizontalStacked',
          ].includes(newChart)
        ) {
          keepOldData(propKey, false);
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          if (oldChartAxes[1].fields.length > 0) {
            newChartAxes[3].fields = assignWithLimitCheck(newChart, 3, oldChartAxes[1].fields);
          }

          return { newChartAxes, callAPI: true };
        }

        break;
      case 'gauge':
      case 'simplecard':
        if (
          newChart === 'gauge' ||
          newChart === 'richText' ||
          newChart === 'simplecard' ||
          newChart === 'funnel'
        ) {
          return { newChartAxes: oldChartAxes, callAPI: measureModified };
        }

        if (
          [
            'line',
            'area',
            'stackedArea',
            'multibar',
            'stackedBar',
            'horizontalBar',
            'horizontalStacked',
          ].includes(newChart)
        ) {
          keepOldData(propKey, false);
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          if (oldChartAxes[1].fields.length > 0) {
            newChartAxes[3].fields = assignWithLimitCheck(newChart, 3, oldChartAxes[1].fields);
          }

          return { newChartAxes, callAPI: true };
        }

        if (
          [
            'waterfall',
            'table',
            'pie',
            'donut',
            'rose',
            'calendar',
            'treeMap',
            'filledMap',
            'sankey',
            'wordCloud',
          ].includes(newChart)
        ) {
          keepOldData(propKey, false);
          if (oldChartAxes[1].fields.length > 0)
            newChartAxes[2].fields = getFieldsToChartAllowedNumbers(
              newChart,
              2,
              oldChartAxes[1].fields,
            );

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          return { newChartAxes, callAPI: true };
        }

        if (newChart === 'scatterPlot') {
          keepOldData(propKey, false);
          if (oldChartAxes[1].fields.length > 0) {
            newChartAxes[2].fields.push(oldChartAxes[1].fields.shift());
          }

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          return { newChartAxes, callAPI: true };
        }

        if (
          newChart === 'crossTab' ||
          newChart === 'boxPlot' ||
          newChart === 'bubbleMap' ||
          newChart === 'heatmap'
        ) {
          keepOldData(propKey, false);

          if (oldChartAxes[1].fields.length > 0) newChartAxes[3].fields = oldChartAxes[1].fields;

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          let callAPI = measureModified;
          if (newChart !== 'crossTab') {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        break;
      case 'heatmap':
      case 'boxPlot':
        if (
          newChart === 'heatmap' ||
          newChart === 'crossTab' ||
          newChart === 'boxPlot' ||
          newChart === 'bubbleMap'
        ) {
          keepOldData(propKey, false);

          // Axis 0
          newChartAxes[0].fields = oldChartAxes[0].fields;

          // bubble map
          if (newChart === 'bubbleMap') {
            const mergedFields = [
              ...(oldChartAxes[1]?.fields || []),
              ...(oldChartAxes[2]?.fields || []),
            ];

            if (mergedFields.length === 1) {
              const field = mergedFields[0];

              if (field?.dataType === 'text') {
                newChartAxes[2].fields = [field];
              } else {
                toast.warning('Non-compatible fields are removed', {
                  position: 'top-center',
                  autoClose: 10000,
                  toastId: 'chart-data-limit-warning',
                  style: { top: '10px' },
                });
              }
            } else if (mergedFields.length > 1) {
              const textFields = mergedFields.filter((f: any) => f?.dataType === 'text');

              if (textFields.length > 0) {
                newChartAxes[2].fields = [textFields[0]];

                const remainingFields =
                  textFields.length > 0
                    ? mergedFields.filter((f: any) => f !== textFields[0])
                    : mergedFields;

                if (remainingFields.length > 0) {
                  newChartAxes[1].fields = [remainingFields[0]];
                }
              } else {
                toast.warning('Non-compatible fields are removed', {
                  position: 'top-center',
                  autoClose: 10000,
                  toastId: 'chart-data-limit-warning',
                  style: { top: '10px' },
                });
                newChartAxes[1].fields = [];
                newChartAxes[2].fields = [];
              }
            }
          }

          //(heatmap, boxPlot, crossTab)
          else {
            const mergedFields = [
              ...(oldChartAxes[1]?.fields || []),
              ...(oldChartAxes[2]?.fields || []),
            ];

            if (mergedFields.length === 1) {
              newChartAxes[1].fields = getFieldsToChartAllowedNumbers(newChart, 1, [
                mergedFields[0],
              ]);
            }

            if (mergedFields.length > 1) {
              if (newChart === 'crossTab') {
                newChartAxes[1].fields = getFieldsToChartAllowedNumbers(newChart, 1, mergedFields);
              } else {
                newChartAxes[1].fields = getFieldsToChartAllowedNumbers(newChart, 1, [
                  mergedFields[0],
                ]);

                newChartAxes[2].fields = getFieldsToChartAllowedNumbers(newChart, 2, [
                  mergedFields[1],
                ]);
              }
            }
          }

          // Axis 3 (measure)
          newChartAxes[3].fields = oldChartAxes[3].fields;

          return { newChartAxes, callAPI: measureModified };
        }

        if (
          [
            'line',
            'area',
            'stackedArea',
            'multibar',
            'stackedBar',
            'horizontalBar',
            'horizontalStacked',
          ].includes(newChart)
        ) {
          keepOldData(propKey, false);
          let mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];

          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          if (mergedFields.length === 1) {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(newChart, 1, [mergedFields[0]]);
          }

          if (mergedFields.length > 1) {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(newChart, 1, [mergedFields[0]]);

            newChartAxes[2].fields = getFieldsToChartAllowedNumbers(newChart, 2, [mergedFields[1]]);
          }

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[3].fields = assignWithLimitCheck(newChart, 3, oldChartAxes[3].fields);
          }

          let callAPI = measureModified;
          if (
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length !== oldChartAxes[2].fields.length ||
            newChartAxes[3].fields.length !== oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (
          [
            'waterfall',
            'table',
            'pie',
            'donut',
            'rose',
            'treeMap',
            'filledMap',
            'sankey',
            'wordCloud',
          ].includes(newChart)
        ) {
          keepOldData(propKey, false);

          const mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];

          if (oldChartAxes[0].fields.length > 0) {
            newChartAxes[0].fields = oldChartAxes[0].fields;
          }

          let hasInvalid = false;

          const axis1Fields =
            newChart === 'filledMap'
              ? oldChartAxes[1].fields.filter((f: any) => {
                  if (f.dataType !== 'text') hasInvalid = true;
                  return f.dataType === 'text';
                })
              : oldChartAxes[1].fields;

          const axis2Fields =
            newChart === 'filledMap'
              ? oldChartAxes[2].fields.filter((f: any) => {
                  if (f.dataType !== 'text') hasInvalid = true;
                  return f.dataType === 'text';
                })
              : oldChartAxes[2].fields;

          newChartAxes[1].fields = getFieldsToChartAllowedNumbers(
            newChart,
            1,
            axis1Fields,
            axis2Fields,
          );

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[2].fields = getFieldsToChartAllowedNumbers(
              newChart,
              2,
              oldChartAxes[3].fields,
            );
          }

          if (hasInvalid && newChart === 'filledMap') {
            toast.warning('Non-compatible fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }

          let callAPI = measureModified;
          if (
            newChartAxes[1].fields.length !==
              oldChartAxes[1].fields.length + oldChartAxes[2].fields.length ||
            newChartAxes[2].fields.length !== oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }
          const finalPlacedFields = [
            ...(newChartAxes[1]?.fields || []),
            ...(newChartAxes[2]?.fields || []),
          ];

          const removedFields = mergedFields.filter((field) => !finalPlacedFields.includes(field));

          if (removedFields.length > 0) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'calendar') {
          const mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];

          const dateField = mergedFields.find(
            (f: any) => f.dataType === 'date' || f.dataType === 'timestamp',
          );

          if (oldChartAxes[1].fields.length > 0) {
            if (dateField) {
              keepOldData(propKey, false);
              updateChartData(propKey, '');

              newChartAxes[0].fields = oldChartAxes[0].fields;

              newChartAxes[1].fields = [
                {
                  dataType: dateField.dataType,
                  displayname: 'date of ' + dateField.fieldname,
                  fieldname: dateField.fieldname,
                  tableId: dateField.tableId,
                  uId: dateField.uId,
                  timeGrain: 'date',
                },
              ];

              if (mergedFields.length > 1) {
                toast.warning('Extra fields are removed', {
                  position: 'top-center',
                  autoClose: 10000,
                  toastId: 'chart-data-limit-warning',
                  style: { top: '10px' },
                });
              }

              if (oldChartAxes[3].fields.length > 0)
                newChartAxes[2].fields = oldChartAxes[3].fields;

              let callAPI = measureModified;
              if (
                newChartAxes[1].fields.length !== mergedFields.length ||
                newChartAxes[2].fields.length !== oldChartAxes[3].fields.length ||
                mergedFields[0].timeGrain !== 'date'
              ) {
                callAPI = true;
              }

              return { newChartAxes, callAPI };
            } else {
              keepOldData(propKey, false);

              newChartAxes[0].fields = oldChartAxes[0].fields;
              newChartAxes[1].fields = [];

              toast.warning('Non-compatible fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-data-limit-warning',
                style: { top: '10px' },
              });

              if (oldChartAxes[3].fields.length > 0)
                newChartAxes[2].fields.push(oldChartAxes[3].fields[0]);

              return { newChartAxes, callAPI: true };
            }
          } else if (oldChartAxes[2].fields.length > 0) {
            if (
              mergedFields[0] &&
              (mergedFields[0].dataType === 'date' || mergedFields[0].dataType === 'timestamp')
            ) {
              keepOldData(propKey, false);
              updateChartData(propKey, '');

              newChartAxes[0].fields = oldChartAxes[0].fields;

              newChartAxes[1].fields = [
                {
                  dataType: dateField.dataType,
                  displayname: 'date of ' + dateField.fieldname,
                  fieldname: dateField.fieldname,
                  tableId: dateField.tableId,
                  uId: dateField.uId,
                  timeGrain: 'date',
                },
              ];

              if (mergedFields.length > 1) {
                toast.warning('Extra fields are removed', {
                  position: 'top-center',
                  autoClose: 10000,
                  toastId: 'chart-data-limit-warning',
                  style: { top: '10px' },
                });
              }

              if (oldChartAxes[3].fields.length > 0)
                newChartAxes[2].fields = oldChartAxes[3].fields;

              let callAPI = measureModified;
              if (
                newChartAxes[1].fields.length !== mergedFields.length ||
                newChartAxes[2].fields.length !== oldChartAxes[3].fields.length ||
                // mergedFields[0].timeGrain !== 'date'
                dateField.timeGrain !== 'date'
              ) {
                callAPI = true;
              }

              return { newChartAxes, callAPI };
            } else {
              keepOldData(propKey, false);

              newChartAxes[0].fields = oldChartAxes[0].fields;
              newChartAxes[1].fields = [];

              toast.warning('Non-compatible fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-data-limit-warning',
                style: { top: '10px' },
              });

              if (oldChartAxes[3].fields.length > 0)
                newChartAxes[2].fields.push(oldChartAxes[3].fields[0]);

              return { newChartAxes, callAPI: true };
            }
          } else {
            keepOldData(propKey, false);

            newChartAxes[0].fields = oldChartAxes[0].fields;
            newChartAxes[1].fields = [];

            if (oldChartAxes[3].fields.length > 0)
              newChartAxes[2].fields.push(oldChartAxes[3].fields[0]);

            let callAPI = measureModified;
            if (newChartAxes[2].fields.length !== oldChartAxes[3].fields.length) {
              callAPI = true;
            }

            return { newChartAxes, callAPI };
          }
        }

        if (newChart === 'scatterPlot') {
          keepOldData(propKey, false);
          const mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          newChartAxes[1].fields = getFieldsToChartAllowedNumbers(
            newChart,
            1,
            oldChartAxes[1].fields,
            oldChartAxes[2].fields,
          );

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[2].fields = getFieldsToChartAllowedNumbers(
              newChart,
              2,
              oldChartAxes[3].fields,
            );
          }

          if (oldChartAxes[3].fields.length > 1) {
            newChartAxes[3].fields = assignWithLimitCheck(
              newChart,
              3,
              oldChartAxes[3].fields.slice(1),
            );
          }

          if (mergedFields.length > 1) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }

          let callAPI = measureModified;
          if (
            newChartAxes[1].fields.length !==
              oldChartAxes[1].fields.length + oldChartAxes[2].fields.length ||
            newChartAxes[2].fields.length + newChartAxes[3].fields.length !==
              oldChartAxes[3].fields.length ||
            oldChartAxes[3].fields.length !== 2
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'funnel') {
          keepOldData(propKey, false);
          const mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          if (oldChartAxes[3].fields.length > 0)
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(
              newChart,
              1,
              oldChartAxes[3].fields,
            );

          if (mergedFields.length > 0) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }

          let callAPI = measureModified;
          if (
            newChartAxes[1].fields.length !== oldChartAxes[3].fields.length ||
            oldChartAxes[1].fields.length > 0 ||
            oldChartAxes[2].fields.length > 0
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'gauge' || newChart === 'richText' || newChart === 'simplecard') {
          keepOldData(propKey, false);
          const mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          if (oldChartAxes[3].fields.length > 0)
            newChartAxes[1].fields.push(oldChartAxes[3].fields[0]);

          const finalPlacedFields = [
            ...(newChartAxes[1]?.fields || []),
            ...(newChartAxes[2]?.fields || []),
          ];
          const removedFields = mergedFields.filter((field) => !finalPlacedFields.includes(field));

          if (removedFields.length > 0) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }

          let callAPI = measureModified;
          if (
            newChartAxes[1].fields.length !== oldChartAxes[3].fields.length ||
            oldChartAxes[1].fields.length > 0 ||
            oldChartAxes[2].fields.length > 0
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        break;

      case 'crossTab' /*  PRAKASH  Demo Fixes 29Nov2024 */:
        if (newChart === 'crossTab' || newChart === 'boxPlot' || newChart === 'bubbleMap') {
          if (newChart !== 'crossTab') {
            let callAPI = measureModified;
            keepOldData(propKey, false);

            let mergedFields = [
              ...(oldChartAxes[1]?.fields || []),
              ...(oldChartAxes[2]?.fields || []),
            ];

            if (mergedFields.length > 2) {
              toast.warning('Extra fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-data-limit-warning',
                style: { top: '10px' },
              });
            }

            newChartAxes[1].fields = [];
            newChartAxes[2].fields = [];

            // Bubble Map
            if (newChart === 'bubbleMap') {
              const textField = mergedFields.find((field: any) => field?.dataType === 'text');

              if (textField) {
                // text goes to axis[2]
                newChartAxes[2].fields = [textField];

                // remaining field goes to axis[1]
                const remainingField = mergedFields.find((field: any) => field !== textField);

                if (remainingField) {
                  newChartAxes[1].fields = [remainingField];
                }
              } else if (mergedFields.length > 0) {
                toast.warning('Non-compatible fields are removed', {
                  position: 'top-center',
                  autoClose: 10000,
                  toastId: 'chart-data-limit-warning',
                  style: { top: '10px' },
                });
              }
            } else if (newChart === 'boxPlot') {
              if (mergedFields[0]) {
                newChartAxes[1].fields = [mergedFields[0]];
              }

              if (mergedFields[1]) {
                newChartAxes[2].fields = [mergedFields[1]];
              }
            }

            // Other charts logic
            else {
              if (oldChartAxes[2]?.fields?.length > 0) {
                newChartAxes[2].fields = getFieldsToChartAllowedNumbers(
                  newChart,
                  2,
                  oldChartAxes[2].fields,
                );
              }

              if (oldChartAxes[1]?.fields?.length > 0) {
                newChartAxes[1].fields = getFieldsToChartAllowedNumbers(
                  newChart,
                  1,
                  oldChartAxes[1].fields,
                );
              }
            }

            if (oldChartAxes[3]?.fields.length > 0) {
              newChartAxes[3].fields = assignWithLimitCheck(newChart, 3, oldChartAxes[3].fields);

              const allowedFieldNumber = getAllowedMeasureFields(newChart);

              const { newFields, callQueryAPI } = convertDateMinMaxToCount(
                oldChartAxes[3].fields,
                allowedFieldNumber,
              );

              newChartAxes[3].fields = [...newFields];

              if (callQueryAPI === true) callAPI = true;
            }

            if (oldChartAxes[0].fields.length > 0) {
              newChartAxes[0].fields = oldChartAxes[0].fields;
            }

            if (
              newChartAxes[1].fields.length === 0 ||
              newChartAxes[2].fields.length === 0 ||
              newChartAxes[3].fields.length === 0
            ) {
              callAPI = true;
            }

            return { newChartAxes, callAPI };
          }

          // crossTab
          else {
            return {
              newChartAxes: oldChartAxes,
              callAPI: measureModified,
            };
          }
        }

        if (
          [
            'waterfall',
            'table',
            'pie',
            'donut',
            'rose',
            'filledMap',
            'treeMap',
            'sankey',
            'wordCloud',
          ].includes(newChart)
        ) {
          // Map filter to Filter
          let callAPI = measureModified;
          keepOldData(propKey, false);

          if (oldChartAxes[0].fields.length > 0) {
            newChartAxes[0].fields = oldChartAxes[0].fields;
          }

          let mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];

          if (newChart === 'filledMap') {
            const textFields = mergedFields.filter((f: any) => f.dataType === 'text');

            if (textFields.length !== mergedFields.length) {
              toast.warning('Non-compatible fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-data-limit-warning',
                style: { top: '10px' },
              });
              callAPI = true;
            }

            mergedFields = textFields;
          }

          newChartAxes[1].fields = assignWithLimitCheck(newChart, 1, mergedFields);

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[2].fields = assignWithLimitCheck(newChart, 2, oldChartAxes[3].fields);

            if (newChart !== 'table') {
              // change agg and display name
              const allowedFieldNumber = getAllowedMeasureFields(newChart);
              const { newFields, callQueryAPI } = convertDateMinMaxToCount(
                oldChartAxes[3].fields,
                allowedFieldNumber,
              );
              newChartAxes[2].fields = [...newFields];
              if (callQueryAPI === true) callAPI = true;
            }
          }

          if (
            newChartAxes[1].fields.length !==
              oldChartAxes[1].fields.length + oldChartAxes[2].fields.length ||
            newChartAxes[2].fields.length !== oldChartAxes[3].fields.length ||
            newChartAxes[1].fields.length === 0 ||
            newChartAxes[2].fields.length === 0
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (
          [
            'line',
            'area',
            'stackedArea',
            'multibar',
            'stackedBar',
            'horizontalBar',
            'horizontalStacked',
          ].includes(newChart)
        ) {
          keepOldData(propKey, false);

          let mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];

          // keep the filter fields
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          if (oldChartAxes[1].fields.length > 0) {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(
              newChart,
              1,
              oldChartAxes[1].fields,
            );
          }

          if (mergedFields.length > 2) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }

          if (oldChartAxes[3].fields.length >= 2) {
            newChartAxes[1].fields = assignWithLimitCheck(newChart, 1, mergedFields);
          } else {
            if (mergedFields.length === 1) {
              newChartAxes[1].fields = assignWithLimitCheck(newChart, 1, mergedFields);
              newChartAxes[2].fields = [];
            } else if (mergedFields.length > 1) {
              newChartAxes[1].fields = assignWithLimitCheck(newChart, 1, [mergedFields[0]]);

              newChartAxes[2].fields = assignWithLimitCheck(newChart, 2, [mergedFields[1]]);
            }
          }

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[3].fields = assignWithLimitCheck(newChart, 3, oldChartAxes[3].fields);
          }

          let callAPI = measureModified;

          // Change min/max agg with date to count
          const allowedFieldNumber = getAllowedMeasureFields(newChart);
          const { newFields, callQueryAPI } = convertDateMinMaxToCount(
            newChartAxes[3].fields,
            allowedFieldNumber,
          );
          newChartAxes[3].fields = [...newFields];

          if (
            callQueryAPI ||
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length !== oldChartAxes[2].fields.length ||
            newChartAxes[3].fields.length !== oldChartAxes[3].fields.length ||
            newChartAxes[1].fields.length === 0 ||
            newChartAxes[3].fields.length === 0
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'calendar') {
          let callAPI = measureModified;
          keepOldData(propKey, false);

          let mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];

          if (oldChartAxes[0].fields.length > 0) {
            newChartAxes[0].fields = oldChartAxes[0].fields;
          }

          if (mergedFields.length > 0) {
            const row = oldChartAxes[1].fields.filter((el: any) => {
              return el.dataType === 'date' || el.dataType === 'timestamp';
            });

            const column = oldChartAxes[2].fields.filter((el: any) => {
              return el.dataType === 'date' || el.dataType === 'timestamp';
            });

            if (row.length + column.length === 0) {
              toast.warning('Non-compatible fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-data-limit-warning',
                style: { top: '10px' },
              });
            }

            newChartAxes[1].fields = assignWithLimitCheck(newChart, 1, [...row, ...column]);

            if (
              oldChartAxes[1].fields.length + oldChartAxes[2].fields.length >
                newChartAxes[1].fields.length &&
              newChartAxes[1].fields.length > 0
            ) {
              toast.warning('Extra fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-data-limit-warning',
                style: { top: '10px' },
              });
            }

            if (newChartAxes[1].fields.length > 0) {
              if (newChartAxes[1].fields[0].timeGrain !== 'date') {
                updateChartData(propKey, '');
                callAPI = true;

                newChartAxes[1].fields = [
                  {
                    dataType: newChartAxes[1].fields[0].dataType,
                    displayname: 'date of ' + newChartAxes[1].fields[0].fieldname,
                    fieldname: newChartAxes[1].fields[0].fieldname,
                    tableId: newChartAxes[1].fields[0].tableId,
                    uId: newChartAxes[1].fields[0].uId,
                    timeGrain: 'date',
                  },
                ];
              }
            }
          }

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[2].fields = getFieldsToChartAllowedNumbers(
              newChart,
              2,
              oldChartAxes[3].fields,
            );

            const allowedFieldNumber = getAllowedMeasureFields(newChart);

            const { newFields, callQueryAPI } = convertDateMinMaxToCount(
              oldChartAxes[3].fields,
              allowedFieldNumber,
            );

            newChartAxes[2].fields = [...newFields];

            if (callQueryAPI === true) {
              callAPI = true;
            }
          }

          if (
            newChartAxes[1].fields.length !==
              oldChartAxes[1].fields.length + oldChartAxes[2].fields.length ||
            newChartAxes[2].fields.length !== oldChartAxes[3].fields.length ||
            newChartAxes[1].fields.length === 0 ||
            newChartAxes[2].fields.length === 0
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'scatterPlot') {
          let callAPI = measureModified;
          keepOldData(propKey, false);
          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          newChartAxes[1].fields = assignWithLimitCheck(
            newChart,
            1,
            oldChartAxes[1].fields.concat(
              oldChartAxes[2].fields,
            ) /*  PRAKASH  Demo Fixes 29Nov2024 */,
          );
          let notAllowedNames: string[] = [];
          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[2].fields = getFieldsToChartAllowedNumbers(
              newChart,
              2,
              oldChartAxes[3].fields,
            );

            //change agg and display name
            let field = JSON.parse(JSON.stringify(oldChartAxes[3].fields[0]));
            if (field.timeGrain === 'date' && (field.agg === 'max' || field.agg === 'min')) {
              callAPI = true;
              newChartAxes[2].fields[0].agg = 'count';
              const updatedField = setDisplayName(newChartAxes[2].fields[0], 'Measure', chartType);
              const uniqueDisplayName = findNewDisplayName(
                chartAxes,
                updatedField,
                undefined,
                notAllowedNames,
              );
              notAllowedNames.push(uniqueDisplayName);
              newChartAxes[2].fields[0].displayname = uniqueDisplayName;
              if (oldChartAxes[3].fields.length === 1) {
                ['labelFormats', 'tooltipFormats'].map((formatName) => {
                  const formatObject = chartControls.properties[propKey].formatOptions[formatName];
                  dispatch(
                    updateFormatOption(propKey, formatName, 'measureFormats', {
                      ...formatObject.measureFormats,
                      [newChartAxes[2].fields[0].uId]: {
                        ...formatObject.measureFormats[newChartAxes[2].fields[0].uId],
                        roundingDigits: 0,
                      },
                    }),
                  );
                });
              }
            }
          }

          if (oldChartAxes[3].fields.length > 1) {
            newChartAxes[3].fields = assignWithLimitCheck(
              newChart,
              3,
              oldChartAxes[3].fields.slice(1),
            );
            oldChartAxes[3].fields.forEach((field: any) => {
              if (field.timeGrain === 'date' && (field.agg === 'max' || field.agg === 'min')) {
                callAPI = true;
                if (newChartAxes[3]?.fields?.length > 0) {
                  newChartAxes[3].fields[0].agg = 'count';
                  const updatedField = setDisplayName(
                    newChartAxes[3].fields[0],
                    'Measure',
                    chartType,
                  );
                  const uniqueDisplayName = findNewDisplayName(
                    chartAxes,
                    updatedField,
                    undefined,
                    notAllowedNames,
                  );
                  notAllowedNames.push(uniqueDisplayName);
                  newChartAxes[3].fields[0].displayname = uniqueDisplayName;
                  ['labelFormats', 'tooltipFormats'].map((formatName) => {
                    const formatObject =
                      chartControls.properties[propKey].formatOptions[formatName];
                    dispatch(
                      updateFormatOption(propKey, formatName, 'measureFormats', {
                        ...formatObject.measureFormats,
                        [newChartAxes[2].fields[0].uId]: {
                          ...formatObject.measureFormats[newChartAxes[2].fields[0].uId],
                          roundingDigits: 0,
                        },
                        [newChartAxes[3].fields[0].uId]: {
                          ...formatObject.measureFormats[newChartAxes[3].fields[0].uId],
                          roundingDigits: 0,
                        },
                      }),
                    );
                  });
                }
              }
            });
          }
          if (
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length + newChartAxes[3].fields.length !==
              oldChartAxes[3].fields.length ||
            newChartAxes[1].fields.length === 0 ||
            newChartAxes[2].fields.length === 0 ||
            newChartAxes[3].fields.length === 0
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'funnel' || newChart === 'simplecard') {
          let callAPI = measureModified;
          keepOldData(propKey, false);
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          if (oldChartAxes[1].fields.length || oldChartAxes[2].fields.length >= 1) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[1].fields = assignWithLimitCheck(newChart, 1, oldChartAxes[3].fields);

            //change agg and display name
            const allowedFieldNumber = getAllowedMeasureFields(newChart);
            const { newFields, callQueryAPI } = convertDateMinMaxToCount(
              oldChartAxes[3].fields,
              allowedFieldNumber,
            );
            newChartAxes[1].fields = [...newFields];
            if (callQueryAPI === true) {
              callAPI = true;
            }
          }
          if (
            oldChartAxes[1].fields.length > 0 ||
            oldChartAxes[2].fields.length > 0 ||
            newChartAxes[1].fields.length !== oldChartAxes[3].fields.length ||
            newChartAxes[1].fields.length === 0
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'gauge' || newChart === 'richText') {
          let callAPI = measureModified;
          keepOldData(propKey, false);

          let mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          if (mergedFields.length >= 1) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[1].fields = assignWithLimitCheck(newChart, 3, oldChartAxes[3].fields);

            //change agg and display name
            const allowedFieldNumber = getAllowedMeasureFields(newChart);
            const { newFields, callQueryAPI } = convertDateMinMaxToCount(
              oldChartAxes[3].fields,
              allowedFieldNumber,
            );
            newChartAxes[1].fields = [...newFields];
            if (callQueryAPI === true) {
              callAPI = true;
            }
          }
          if (
            oldChartAxes[1].fields.length > 0 ||
            oldChartAxes[2].fields.length > 0 ||
            newChartAxes[1].fields.length !== oldChartAxes[3].fields.length ||
            newChartAxes[1].fields.length === 0
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'heatmap') {
          let callAPI = measureModified;
          keepOldData(propKey, false);

          let mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];

          if (mergedFields.length > 1) {
            newChartAxes[1].fields = assignWithLimitCheck(newChart, 1, [mergedFields[0]]);
            newChartAxes[2].fields = assignWithLimitCheck(newChart, 2, [mergedFields[1]]);
          } else {
            if (oldChartAxes[1].fields.length > 0) {
              newChartAxes[1].fields = assignWithLimitCheck(newChart, 1, [mergedFields[0]]);
            }
            if (oldChartAxes[2].fields.length > 0) {
              newChartAxes[1].fields = assignWithLimitCheck(newChart, 1, [mergedFields[0]]);
            }
          }

          const finalPlacedFields = [
            ...(newChartAxes[1]?.fields || []),
            ...(newChartAxes[2]?.fields || []),
          ];

          const removedFields = mergedFields.filter((field) => !finalPlacedFields.includes(field));

          if (removedFields.length > 0) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[3].fields = assignWithLimitCheck(newChart, 3, oldChartAxes[3].fields);

            //change agg and display name
            const allowedFieldNumber = getAllowedMeasureFields(newChart);
            const { newFields, callQueryAPI } = convertDateMinMaxToCount(
              oldChartAxes[3].fields,
              allowedFieldNumber,
            );
            newChartAxes[3].fields = [...newFields];
            if (callQueryAPI === true) {
              callAPI = true;
            }
          }

          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          if (
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length !== oldChartAxes[2].fields.length ||
            newChartAxes[3].fields.length !== oldChartAxes[3].fields.length ||
            newChartAxes[1].fields.length === 0 ||
            newChartAxes[2].fields.length === 0 ||
            newChartAxes[3].fields.length === 0
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }
        break;

      case 'bubbleMap':
        if (
          newChart === 'crossTab' ||
          newChart === 'boxPlot' ||
          newChart === 'bubbleMap' ||
          newChart === 'heatmap'
        ) {
          keepOldData(propKey, false);
          const mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];
          newChartAxes[0].fields = oldChartAxes[0].fields;
          newChartAxes[3].fields = oldChartAxes[3].fields;
          if (newChart === 'bubbleMap') {
            newChartAxes[1].fields = oldChartAxes[1].fields;
            newChartAxes[2].fields = oldChartAxes[2].fields;
          } else if (newChart === 'crossTab') {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(newChart, 1, mergedFields);
          } else {
            newChartAxes[1].fields = oldChartAxes[2].fields;
            newChartAxes[2].fields = oldChartAxes[1].fields;
          }

          let callAPI = measureModified;
          if (
            (newChart === 'boxPlot' || newChart === 'heatmap') &&
            (newChartAxes[1].fields.length === 0 ||
              newChartAxes[2].fields.length === 0 ||
              newChartAxes[3].fields.length === 0)
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (
          [
            'line',
            'area',
            'stackedArea',
            'multibar',
            'stackedBar',
            'horizontalBar',
            'horizontalStacked',
          ].includes(newChart)
        ) {
          keepOldData(propKey, false);

          let mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];
          if (oldChartAxes[0].fields.length > 0) {
            newChartAxes[0].fields = oldChartAxes[0].fields;
          }

          if (mergedFields.length === 1) {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(newChart, 1, [mergedFields[0]]);
          }

          if (mergedFields.length > 1) {
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(newChart, 1, [mergedFields[0]]);
            newChartAxes[2].fields = getFieldsToChartAllowedNumbers(newChart, 2, [mergedFields[1]]);
          }

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[3].fields = assignWithLimitCheck(newChart, 3, oldChartAxes[3].fields);
          }

          const finalPlacedFields = [
            ...(newChartAxes[1]?.fields || []),
            ...(newChartAxes[2]?.fields || []),
          ];

          const removedFields = mergedFields.filter((field) => !finalPlacedFields.includes(field));

          if (removedFields.length > 0) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }

          let callAPI = measureModified;
          if (
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length !== oldChartAxes[2].fields.length ||
            newChartAxes[3].fields.length !== oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (
          [
            'waterfall',
            'table',
            'pie',
            'donut',
            'rose',
            'filledMap',
            'treeMap',
            'filledMap',
            'sankey',
            'wordCloud',
          ].includes(newChart)
        ) {
          // Map filter to Filter
          keepOldData(propKey, false);
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          let mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];

          if (newChart === 'filledMap') {
            const axis1TextFields = oldChartAxes[1].fields.filter(
              (f: any) => f.dataType === 'text',
            );
            const hasNonText = axis1TextFields.length !== oldChartAxes[1].fields.length;

            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(
              newChart,
              1,
              oldChartAxes[2].fields,
              axis1TextFields,
            );

            if (hasNonText && oldChartAxes[2].fields < 1) {
              toast.warning('Non-compatible fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-data-limit-warning',
                style: { top: '10px' },
              });
            } else if (mergedFields.length > 1) {
              toast.warning('Extra fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-data-limit-warning',
                style: { top: '10px' },
              });
            }
          } else {
            newChartAxes[1].fields = assignWithLimitCheck(
              newChart,
              1,
              oldChartAxes[2].fields,
              oldChartAxes[1].fields,
            );
          }
          if (oldChartAxes[3].fields.length > 0)
            newChartAxes[2].fields = getFieldsToChartAllowedNumbers(
              newChart,
              2,
              oldChartAxes[3].fields,
            );

          let callAPI = measureModified;
          if (
            newChartAxes[1].fields.length !==
              oldChartAxes[1].fields.length + oldChartAxes[2].fields.length ||
            newChartAxes[2].fields.length !== oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }
        if (newChart === 'calendar') {
          let callAPI = measureModified;
          keepOldData(propKey, false);

          if (oldChartAxes[0].fields.length > 0) {
            newChartAxes[0].fields = oldChartAxes[0].fields;
          }

          const mergedFields = [...oldChartAxes[1].fields, ...oldChartAxes[2].fields];

          const dateFields = mergedFields.filter(
            (el: any) => el.dataType === 'date' || el.dataType === 'timestamp',
          );

          if (dateFields.length > 0) {
            newChartAxes[1].fields = [
              {
                dataType: dateFields[0].dataType,
                displayname: 'date of ' + dateFields[0].fieldname,
                fieldname: dateFields[0].fieldname,
                tableId: dateFields[0].tableId,
                uId: dateFields[0].uId,
                timeGrain: 'date',
              },
            ];

            if (mergedFields.length > 1) {
              toast.warning('Extra fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-data-limit-warning',
                style: { top: '10px' },
              });
            }

            if (oldChartAxes[3].fields.length > 0) {
              newChartAxes[2].fields = oldChartAxes[3].fields;
            }

            if (
              newChartAxes[1].fields.length !== dateFields.length ||
              newChartAxes[2].fields.length !== oldChartAxes[3].fields.length ||
              dateFields[0].timeGrain !== 'date'
            ) {
              callAPI = true;
            }

            updateChartData(propKey, '');
            return { newChartAxes, callAPI };
          } else {
            const hasFieldsToRemove =
              oldChartAxes[1].fields.length > 0 || oldChartAxes[2].fields.length > 0;
            newChartAxes[1].fields = [];
            if (oldChartAxes[3].fields.length > 0) {
              newChartAxes[2].fields = [oldChartAxes[3].fields[0]];
            }

            if (hasFieldsToRemove) {
              toast.warning('Non-compatible fields are removed', {
                position: 'top-center',
                autoClose: 10000,
                toastId: 'chart-data-limit-warning',
                style: { top: '10px' },
              });
            }
            return { newChartAxes, callAPI: true };
          }
        }
        if (newChart === 'scatterPlot') {
          keepOldData(propKey, false);
          // Map filter to Filter
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          newChartAxes[1].fields = assignWithLimitCheck(
            newChart,
            1,
            oldChartAxes[1].fields,
            oldChartAxes[2].fields,
          );

          if (oldChartAxes[3].fields.length > 0) {
            newChartAxes[2].fields = getFieldsToChartAllowedNumbers(
              newChart,
              2,
              oldChartAxes[3].fields,
            );
          }

          if (oldChartAxes[3].fields.length > 1) {
            newChartAxes[3].fields = assignWithLimitCheck(
              newChart,
              3,
              oldChartAxes[3].fields.slice(1),
            );
          }

          let callAPI = measureModified;
          if (
            newChartAxes[1].fields.length !== oldChartAxes[1].fields.length ||
            newChartAxes[2].fields.length + newChartAxes[3].fields.length !==
              oldChartAxes[3].fields.length ||
            oldChartAxes[3].fields.length !== 2
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'funnel') {
          keepOldData(propKey, false);
          if (oldChartAxes[1].fields.length > 0 || oldChartAxes[2].fields.length > 0) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          if (oldChartAxes[3].fields.length > 0)
            newChartAxes[1].fields = getFieldsToChartAllowedNumbers(
              newChart,
              1,
              oldChartAxes[3].fields,
            );

          let callAPI = measureModified;
          if (
            oldChartAxes[1].fields.length > 0 ||
            oldChartAxes[2].fields.length > 0 ||
            newChartAxes[1].fields.length !== oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        if (newChart === 'gauge' || newChart === 'richText' || newChart === 'simplecard') {
          keepOldData(propKey, false);
          const mergedFields = [
            ...(oldChartAxes[1]?.fields || []),
            ...(oldChartAxes[2]?.fields || []),
          ];
          if (oldChartAxes[0].fields.length > 0) newChartAxes[0].fields = oldChartAxes[0].fields;

          if (mergedFields) {
            toast.warning('Extra fields are removed', {
              position: 'top-center',
              autoClose: 10000,
              toastId: 'chart-data-limit-warning',
              style: { top: '10px' },
            });
          }

          if (oldChartAxes[3].fields.length > 0)
            newChartAxes[1].fields.push(oldChartAxes[3].fields[0]);

          let callAPI = measureModified;
          if (
            oldChartAxes[1].fields.length > 0 ||
            oldChartAxes[2].fields.length > 0 ||
            newChartAxes[1].fields.length !== oldChartAxes[3].fields.length
          ) {
            callAPI = true;
          }

          return { newChartAxes, callAPI };
        }

        break;

      case 'richText':
        if (
          [
            'waterfall',
            'filledMap',
            'treeMap',
            'sankey',
            'calendar',
            'filledMap',
            'pie',
            'table',
            'donut',
            'rose',
            'wordCloud',
          ].includes(newChart)
        ) {
          keepOldData(propKey, false);
          newChartAxes[0].fields = [];
          newChartAxes[1].fields = [];
          newChartAxes[2].fields = [];
          return { newChartAxes, callAPI: true };
        }

        if (
          [
            'scatterPlot',
            'heatmap',
            'crossTab',
            'boxPlot',
            'bubbleMap',
            'line',
            'area',
            'stackedArea',
            'multibar',
            'stackedBar',
            'horizontalBar',
            'horizontalStacked',
          ].includes(newChart)
        ) {
          keepOldData(propKey, false);

          newChartAxes[0].fields = [];
          newChartAxes[1].fields = [];
          newChartAxes[2].fields = [];
          newChartAxes[3].fields = [];

          return { newChartAxes, callAPI: true };
        }

        if (newChart === 'funnel' || newChart === 'simplecard' || newChart === 'gauge') {
          newChartAxes[0].fields = [];
          newChartAxes[1].fields = [];
          return { newChartAxes, callAPI: true };
        }

        break;

      default:
        return { newChartAxes: oldChartAxes, callAPI: measureModified };
    }
  };

  return switchAxesForCharts;
};
